/**
 * Phrase-scoped local ASR placement adapter for TT-827.
 *
 * The handler owns rolling-window audio state, bounded ASR inference, and
 * transcript consensus. SpeechStream wiring remains outside this module so the
 * class can be tested without sockets, recorders, or ONNX runtime assets.
 */

import { createStreamingConsensus } from './asr/engine/streaming_consensus';
import { resolveLocalAsrBackend } from './asr/asrBackend';
import { INTERVENTION_STREAM_SLOT } from './asr/vosk/voskAsr';
import { shouldUseLocalPlacementAsr } from './asr/localAsrEnablement';
import { logLocalAsrStreamingTranscript } from './asr/localAsrDebug';
import { recordLocalAsrLifecycle, recordLocalAsrFailure, LOCAL_ASR_FALLBACK_REASON } from './asr/localAsrLifecycle';
import { placementTimeMs } from './placementClock';
import { BIASED_GRAPH_SCOPE_INTERVENTION, hasBiasedGraphDescriptor } from './asr/vosk/voskBiasedGraph';
import { getWarmupNoise } from './asr/warmupNoise';
import { isUnkToken, normalizeWord } from './asr/wordNormalize';
import {
  WARMUP_CAP_MS,
  WARMUP_CONVERGENCE_THRESHOLD,
  WARMUP_MIN_ITERATIONS,
  WARMUP_SLOW_LATCH_COMPARISONS,
  WARMUP_STREAMING_TARGET_RTF,
} from './asr/localAsrWarmupTuning';

export const LOCAL_ASR_MODE_PHRASE = 'phrase';
export const LOCAL_ASR_MODE_INTERVENTION = 'intervention';
export const LOCAL_ASR_SAMPLE_RATE = 16000;
export const MAX_CONSECUTIVE_INFERENCE_FAILURES = 3;
// Per-activity cap on how many inference failures are logged verbatim. The teardown latch
// bounds only CONSECUTIVE failures; a flapping runtime (fail, succeed, fail) resets that
// counter on every success and never tears down, so an uncapped writer appends one timeline
// entry (and console line) per failure for the whole session. The first few carry the
// recoverable cause TT-1173 needs; past this we emit one 'capped' marker and go quiet.
export const MAX_LOGGED_INFERENCE_FAILURES = 5;

/**
 * Why a warmup run stopped. Reported on runtime.warmupEnded so a reader can tell
 * a settled verdict from a run that never reached one — `converged: false` alone
 * conflates "this device is too slow", "the budget ran out mid-measurement" and
 * "a phrase cut the run short", which are three different answers to the routing
 * question TT-1169 asks of this log.
 */
export const WARMUP_VERDICT = Object.freeze({
  FAST: 'fast',
  SLOW: 'slow',
  BUDGET_EXHAUSTED: 'budgetExhausted',
  ERROR: 'error',
  CANCELLED: 'cancelled',
});

const DEFAULT_ROLLING_STRIDE_MS = 200;
// A decode past this fraction of the backend's timeout budget counts as a near-miss on
// decode.phraseStats / decode.interventionStats (TT-1173). A healthy decode is tens of
// ms against an 8s budget, so half the budget is already pathological — counting from
// there surfaces a device grazing the limit (the TT-1158 repro hit 7317ms) before the
// first hard timeout turns it into an inference failure.
//
// A reset stride has TWO regions inside total — a phrase-open flush and the accept — each
// awaiting the SAME full budget (voskAsr reports the flush as flushMs, and _flushStream
// waits on _decodeTimeoutMs just as the accept does). A near-miss is "did EITHER region
// graze its budget", so _recordDecodeTiming counts on the LARGER of the two: measuring the
// accept alone would miss a flush that runs to the edge (the exact TT-1158 shape), and
// measuring total would falsely combine a normal flush with a normal accept.
const DECODE_TIMEOUT_NEAR_MISS_RATIO = 0.5;
const DEFAULT_WINDOW_MS = 1025;
const WAV_HEADER_BYTES = 44;
// Memory guard on samples retained for the summary's distribution. A real run retains
// tens; this only bounds a device fast enough to iterate thousands of times without
// latching. Once it engages the percentiles cover the retained prefix only, which is
// what steadySamples reports.
const WARMUP_SAMPLE_RETENTION_CAP = 512;
const WARMUP_DISPOSE_DRAIN_MS = 2000;
const WARMUP_WORD_LIST = ['the', 'people', 'can', 'read', 'and'];

/**
 * Which decode path the warmup timings were measured on — reported on
 * runtime.warmupEnded so a reader knows whether the numbers describe what real
 * reading uses. `biasedGraph` is the per-phrase HCLG the student actually decodes
 * against; `wordListGrammar` means phrase 0's graph was not available and warmup
 * measured the fallback grammar instead, which is a different workload and must
 * not be pooled with the other in any threshold calibration.
 */
export const WARMUP_PATH = Object.freeze({
  BIASED_GRAPH: 'biasedGraph',
  WORD_LIST: 'wordListGrammar',
});

/**
 * Rounds a ratio to two decimals, or returns null unless both operands are
 * positive. Null means "not measurable", which a consumer must not confuse with
 * a measured extreme — see the tierUpFactor note in startWarmup.
 * @param {number} numerator
 * @param {number} denominator
 * @returns {number|null}
 */
function ratioOrNull(numerator, denominator) {
  if (!(numerator > 0) || !(denominator > 0)) {
    return null;
  }
  return +(numerator / denominator).toFixed(2);
}

/**
 * Nearest-rank percentile of an ascending-sorted array, rounded to whole milliseconds.
 * Nearest-rank rather than interpolated so every reported value is a timing the device
 * really produced, and so stays comparable against alreadyFastMs. Null means not
 * measurable, not zero.
 * @param {number[]} sortedAscending
 * @param {number} fraction Between 0 and 1.
 * @returns {number|null}
 */
function percentileOfSorted(sortedAscending, fraction) {
  if (!sortedAscending.length) {
    return null;
  }
  const rank = Math.ceil(fraction * sortedAscending.length) - 1;
  const index = Math.min(sortedAscending.length - 1, Math.max(0, rank));
  return Math.round(sortedAscending[index]);
}

/**
 * Splits expected phrase text into a passage word list.
 * @param {string} text
 * @returns {string[]}
 */
function wordsFromText(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean);
}

/**
 * Returns the word list provided by metadata or derives it from expected text.
 * @param {object} metadata
 * @returns {string[]}
 */
function getWordList(metadata = {}) {
  if (Array.isArray(metadata.wordList)) {
    return metadata.wordList;
  }
  return wordsFromText(metadata.expectedText);
}

/**
 * The word an intervention is JUDGING, when the caller supplied it.
 *
 * Separate from getWordList, which returns the PHRASE (intervention audio carries the
 * phrase's recording props). The phrase constrains the recognizer; the target only picks
 * which decoded token to hand back. Null when absent — the emit then uses the last token.
 * See docs/tt-828-client-asr-audit-findings.md domain fact #2.
 * @param {object} metadata
 * @returns {string|null}
 */
function getInterventionTarget(metadata = {}) {
  const target = metadata.interventionTarget;
  return typeof target === 'string' && target.trim() ? target : null;
}

/**
 * Chooses the ONE token an intervention emits: the target if the transcript contains it,
 * else the last token.
 *
 * WHICH token matters because `Listen.listenCallback` overwrites `listener.transcript`
 * and recomputes `listener.matches` from it per emission — it never accumulates — so a
 * neighbour emitted after the target erases an earned match and routes the listen to
 * `patter.struggle`. The decode is phrase-constrained, so neighbours are always possible.
 *
 * Bounded, not absolute — but narrowly. `streamResult` is committed segments plus the live
 * partial, and committed text is monotonic, so once the target COMMITS every later stride
 * re-picks it and `_emitTranscript`'s dedupe suppresses the repeat: a latch in practice.
 * The target is lost only if it never commits — which is reachable, since a partial-only
 * target revised away inside the first second is erased before minTimeElapsed even reads the
 * match. Latching "already emitted the target" on the state was still declined, but not for
 * the reason first written down: an intervention-scoped latch dies with _clearState, so it is
 * bounded by the 5s listen window, not permanent. The reason is which error it trades for
 * which. The graph hypothesizes phrase words from noise, so a spurious partial containing the
 * target is plausible, and latching on one converts a bounded false REJECT (one extra
 * intervention) into a bounded false ACCEPT (a student advanced past a word they cannot
 * read). Target preference already widens the rule to "the target appeared anywhere in the
 * window", which a whole-phrase re-read satisfies — pinned by test, as is this exposure.
 *
 * Exact match on normalized tokens: near-misses fall through to last-token, where
 * Listen's fuzzy matcher scores them.
 * @param {string[]} words normalized, UNK-filtered transcript tokens
 * @param {string|null} target the intervention's judged word, unnormalized
 * @returns {string} the token to emit, or '' when there is nothing to emit
 */
function pickInterventionToken(words, target) {
  if (words.length === 0) {
    return '';
  }
  const normalizedTarget = target ? normalizeWord(target) : '';
  if (normalizedTarget && words.includes(normalizedTarget)) {
    return normalizedTarget;
  }
  return words[words.length - 1];
}

/**
 * Resolves the per-story biased-HCLG descriptor for a phrase (or intervention) from
 * its metadata. There is no feature flag: whenever the session is in vosk local-ASR
 * mode and the metadata identifies the phrase (storyId + integer phraseIndex, both
 * already present on the recording props), the descriptor is threaded through to
 * voskAsr.transcribe. The vosk backend decides whether it is usable — runtime
 * graph-load support (vosk-v2) plus bytes primed in the story-graph store — and
 * falls back to the word-list grammar otherwise, so an unusable descriptor can never
 * fail a decode. Interventions carry the same storyId/phraseIndex, so they decode
 * against the current phrase's graph.
 * @param {object} metadata
 * @returns {object|null}
 */
function getBiasedGraphDescriptor(metadata = {}) {
  const descriptor = {
    storyId: metadata.storyId,
    phraseIndex: metadata.phraseIndex,
  };
  return hasBiasedGraphDescriptor(descriptor) ? descriptor : null;
}

/**
 * Resolves the locale value used by local placement eligibility checks.
 * @param {object} metadata
 * @returns {string}
 */
function getLocale(metadata = {}) {
  return metadata.locale || metadata.languageCode;
}

/**
 * Resolves the placement mode used by local placement eligibility checks.
 * @param {object} metadata
 * @returns {string}
 */
function getPlacementMode(metadata = {}) {
  return metadata.placementMode || metadata.mode || LOCAL_ASR_MODE_PHRASE;
}

/**
 * Whether two optional values are both present and different.
 * @param {*} current
 * @param {*} incoming
 * @returns {boolean}
 */
function presentMismatch(current, incoming) {
  return current !== undefined && incoming !== undefined && current !== incoming;
}

/**
 * Reads a Blob into an ArrayBuffer in browsers with or without Blob.arrayBuffer().
 * @param {Blob} blob
 * @returns {Promise<ArrayBuffer>}
 */
function readBlobAsArrayBuffer(blob) {
  if (blob && typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Finds the start and size of the PCM data chunk in a WAV buffer.
 * @param {DataView} view
 * @returns {{offset: number, size: number}}
 */
function getWavDataChunk(view) {
  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const chunkId = String.fromCharCode(
      view.getUint8(offset),
      view.getUint8(offset + 1),
      view.getUint8(offset + 2),
      view.getUint8(offset + 3),
    );
    const chunkSize = view.getUint32(offset + 4, true);
    if (chunkId === 'data') {
      return { offset: offset + 8, size: chunkSize };
    }
    offset += 8 + chunkSize + (chunkSize % 2);
  }

  throw new Error('WAV data chunk not found');
}

/**
 * Parses a 16-bit PCM WAV Blob into mono Float32 samples.
 * @param {Blob} blob
 * @returns {Promise<Float32Array>}
 */
export async function wavBlobToFloat32(blob) {
  const buffer = await readBlobAsArrayBuffer(blob);
  const view = new DataView(buffer);
  const { offset, size } = getWavDataChunk(view);
  const sampleCount = Math.floor(size / 2);
  const samples = new Float32Array(sampleCount);

  for (let i = 0; i < sampleCount; i++) {
    samples[i] = view.getInt16(offset + (i * 2), true) / 32768;
  }

  return samples;
}

// URL param (set by DebugMenu) that re-enables the legacy rolling-window decode
// path. Streaming decode is the default.
export const ASR_WINDOW_MODE_PARAM = 'asr_window';

// Cap on coalesced streaming backlog (~3s of audio). Past this, the oldest
// pending strides are shed — a device persistently decoding slower than
// real-time can't be saved by queueing, only made visible via queueDropCount.
const STREAM_BACKLOG_CAP_SAMPLES = 3 * LOCAL_ASR_SAMPLE_RATE;

/**
 * Concatenates pending streaming strides into one buffer, done once at dequeue.
 * @param {Float32Array[]} chunks
 * @returns {Float32Array}
 */
function concatStreamChunks(chunks) {
  if (chunks.length === 1) {
    return chunks[0];
  }
  let total = 0;
  for (let i = 0; i < chunks.length; i++) total += chunks[i].length;
  const merged = new Float32Array(total);
  let offset = 0;
  for (let i = 0; i < chunks.length; i++) {
    merged.set(chunks[i], offset);
    offset += chunks[i].length;
  }
  return merged;
}

/**
 * Reads the window-mode URL override (`?asr_window=1`, set by DebugMenu).
 * @returns {boolean}
 */
function getWindowDecodeOverride() {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    return new URLSearchParams(window.location.search).get(ASR_WINDOW_MODE_PARAM) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * Which clock a phrase's raw transcript entries are stamped on.
 *
 * `placement` is the axis the `timings` array in `transcript_phrase_N` uses, so those
 * entries can be compared to the final transcript's directly. `phrase` means the
 * placement clock was unavailable when the phrase's first entry landed (no mounted
 * reader, or recording had not started) and every entry is milliseconds-since-phrase-
 * start, which is NOT comparable across logs.
 *
 * The axis is decided by the phrase's first entry and held for the rest of it. Switching
 * mid-phrase used to relabel the whole array while leaving already-stamped entries on the
 * old axis, so a reader was told `phrase` for an array where some entries were
 * story-absolute — worse than not knowing, because the simulator's deltas then go wrong
 * silently. `mixed` is the honest label for the one case that cannot be held: a clock
 * registered at the first entry and gone by a later one (Speech mid-teardown).
 */
const RAW_TIME_AXIS = {
  PLACEMENT: 'placement',
  PHRASE: 'phrase',
  MIXED: 'mixed',
};

/**
 * Creates isolated mutable state for one audio mode.
 * @param {string} mode
 * @returns {object}
 */
function createModeState(mode) {
  return {
    mode,
    active: false,
    eligible: false,
    metadata: {},
    generation: 0,
    rollingBuffer: null,
    strideBuffer: null,
    strideOffset: 0,
    samplesReceivedForActiveWindow: 0,
    chunkIndex: 0,
    consensus: null,
    emittedText: '',
    inFlight: false,
    processingPromise: null,
    latestSnapshot: null,
    consecutiveFailures: 0,
    flushedDone: false,
    streamStarted: false,
    streamDispatched: false,
    lastLoggedRawText: null,
    startedAt: 0,
    rawTranscriptLog: [],
    rawTimeAxis: RAW_TIME_AXIS.PLACEMENT,
    // Per-phrase decode accounting, and null for intervention states. Deliberately NOT
    // inlined on the state: state objects are reused in place across phrases, so counters
    // living here are wiped by _clearState before a still-queued decode can add its cost
    // (see createDecodeStatsRecord, which also covers why interventions get none).
    decodeStats: null,
  };
}

/**
 * Creates the per-phrase decode accounting record read by _reportDecodeStats.
 *
 * Lives OUTSIDE the mode state because mode states are mutated in place across phrases,
 * so counters on the state are zeroed by _clearState the moment the phrase turns over —
 * dropping the cost of any decode still queued in the worker, i.e. exactly the slow-device
 * decodes this measures. Draining before reporting was tried and rejected: it made
 * startPhrase/endPhrase async, and their callers do not await, so a phrase armed behind a
 * slow drain dropped the next phrase's opening audio. A detached record keeps arm/disarm
 * synchronous and still collects late timings, flushed by the owning decode loop on exit.
 *
 * Identity fields are snapshotted here, not read at report time, so a deferred flush
 * attributes its stats to the phrase that produced them.
 *
 * Created for BOTH modes, reported under separate event names (see _reportDecodeStats) —
 * intervention audio shares the phrase's activityId and phraseIndex, so one event would
 * carry two different measurements under one key.
 *
 * Interventions were excluded while they ran window decode; streaming changed what the
 * missing data costs. A shed stride is now audio the decoder never heard, so the stride
 * carrying the answer can be dropped with only queueDropCount to show it; and the
 * intervention's create lands inline on its first decode, sharing that decode's
 * DEFAULT_DECODE_TIMEOUT_MS budget, which stageMs is the only view of.
 * @param {object} metadata
 * @param {boolean} eligible
 * @param {boolean} streaming
 * @param {string} mode LOCAL_ASR_MODE_PHRASE | LOCAL_ASR_MODE_INTERVENTION
 * @returns {object}
 */
function createDecodeStatsRecord(metadata, eligible, streaming, mode) {
  return {
    mode,
    activityId: metadata.activityId,
    phraseIndex: metadata.phraseIndex,
    eligible: Boolean(eligible),
    streaming,
    queueDropCount: 0,
    decodeDispatched: false,
    decodeCount: 0,
    decodeMsTotal: 0,
    decodeMsMax: 0,
    decodeTimeoutNearMisses: 0,
    // Whether any decode reported its timeout budget. NeMo times decodes but carries
    // no timeoutMs, so without this the counter above would report a fast-looking 0
    // on a backend that cannot measure near-misses at all.
    decodeTimeoutBudgetSeen: false,
    audioSamplesDecoded: 0,
    stageMsTotal: 0,
    stagedCount: 0,
    firstDecodeMs: null,
    // Queued snapshots/chunks still in latestSnapshot when the phrase ends. Unlike
    // queueDropCount (mid-phrase shedding at STREAM_BACKLOG_CAP_SAMPLES), these are
    // audio the decoder WOULD have processed had the phrase not ended.
    boundaryDiscardCount: 0,
    // Set by _finalizeDecodeStats when the phrase ends with a decode still in flight;
    // the decode loop reports under this reason once it drains.
    pendingReason: null,
    reported: false,
  };
}

/**
 * LocalPlacementAsrHandler owns fixed-window local ASR transcript assembly.
 */
export class LocalPlacementAsrHandler {
  /**
   * @param {object} [options]
   * @param {Function} [options.createAsr] Defaults to the flag-selected backend (Vosk/NeMo).
   * @param {object|Function} [options.createOptions] Defaults to the selected backend's options.
   * @param {Console|object} [options.logger]
   * @param {Function} [options.createConsensus]
   * @param {Function} [options.isLocalPlacementEligible]
   * @param {boolean} [options.streamDecode] Feed strides to a persistent streaming
   * recognizer (Vosk only) instead of re-decoding a rolling window per stride.
   * Default on; the DebugMenu's `?asr_window=1` override restores window mode.
   */
  constructor({
    createAsr,
    createOptions,
    logger = console,
    createConsensus = createStreamingConsensus,
    isLocalPlacementEligible = shouldUseLocalPlacementAsr,
    streamDecode = !getWindowDecodeOverride(),
  } = {}) {
    // createAsr and createOptions are a matched pair (a backend's factory + its
    // options); injecting only one would pair it with the other backend's half.
    if (Boolean(createAsr) !== Boolean(createOptions)) {
      throw new Error('LocalPlacementAsrHandler: provide both createAsr and createOptions, or neither');
    }
    // Resolve the flag-selected backend only when the caller injected neither.
    const backend = createAsr ? null : resolveLocalAsrBackend();
    this._createAsr = createAsr || backend.createAsr;
    this._createOptions = createOptions || backend.createOptions;
    this._logger = logger;
    this._createConsensus = createConsensus;
    this._isLocalPlacementEligible = isLocalPlacementEligible;
    this._streamDecode = Boolean(streamDecode);
    // Per-activity inference-failure log budget (see MAX_LOGGED_INFERENCE_FAILURES). The
    // count resets when the activityId changes, so each activity keeps its own first-N.
    this._loggedFailureActivityId = undefined;
    this._loggedFailureCount = 0;

    this._asr = null;
    this._ready = false;
    this._disposed = false;
    this._paused = false;
    this._sampleRate = LOCAL_ASR_SAMPLE_RATE;
    this._windowSamples = 0;
    this._strideSamples = 0;
    this._lastPhraseUsedLocalAsr = false;
    this._lastInterventionStartWarningKey = null;

    this._warmupActive = false;
    this._warmupCancelled = false;
    this._warmupConverged = false;
    // One of WARMUP_VERDICT, latched at whichever exit the run takes; null while
    // a run is still in flight.
    this._warmupVerdict = null;
    // True until warmup settles the JIT question one way or the other — the
    // timings plateauing (fast enough or not), the cap expiring, or an inference
    // error. A session gets ONE run, so this latches false at its first settled
    // exit; a cancelled run leaves it true.
    //
    // WRITE-ONLY TODAY. It was the re-arm gate, and the re-arm apparatus went with
    // TT-1209; nothing in src/ reads it now. TT-1169 settled the reconciliation
    // with SpeechStream's once-per-session latch that this field used to disagree
    // with: a run cancelled by live audio now RELEASES that latch (the two agree —
    // warmup is still needed and the next story load re-measures), while a run
    // cancelled by the launch deadline latches a server routing decision and
    // deliberately leaves the latch spent, because re-measuring a device that
    // could not finish one iteration inside cap + 1.5s re-spends loader time on
    // exactly the hardware least able to afford it, for an answer that cannot
    // change. This field stays true in both cancel cases and is kept only as the
    // handler-side record that no settled answer was reached.
    this._warmupNeeded = true;
    this._warmupPromise = null;
    // Mirrors the run's local iteration counter onto the instance, for warmupIterations.
    this._warmupIterations = 0;
    // Cold-start and most-recent inference for the run, feeding tierUpFactor.
    // There is no cross-run counterpart: warmup runs once per session now, so the
    // per-run numbers ARE the session's numbers.
    this._warmupFirstInferenceMs = 0;
    this._warmupLastInferenceMs = 0;
    // Which decode path the run actually measured, resolved before the first
    // iteration — the requested biased graph may not be available.
    this._warmupPath = WARMUP_PATH.WORD_LIST;

    this._states = {
      [LOCAL_ASR_MODE_PHRASE]: createModeState(LOCAL_ASR_MODE_PHRASE),
      [LOCAL_ASR_MODE_INTERVENTION]: createModeState(LOCAL_ASR_MODE_INTERVENTION),
    };

    this._transcriptCallbacks = new Set();
    this._interventionTranscriptCallbacks = new Set();
    this._readyCallbacks = new Set();
    this._errorCallbacks = new Set();
    this._phraseTranscriptLogCallbacks = new Set();
  }

  /**
   * Whether the ASR runtime loaded successfully.
   * @returns {boolean}
   */
  get isReady() {
    return this._ready;
  }

  /**
   * Whether the current phrase can use local placement ASR.
   * @returns {boolean}
   */
  get isPhraseEligible() {
    return Boolean(this._states[LOCAL_ASR_MODE_PHRASE].eligible);
  }

  /**
   * Active backend id (`vosk`/`nemo`), or undefined before init. Exposed so the
   * lifecycle telemetry can report which engine a session actually ran on without
   * resolving the backend a second time.
   * @returns {string|undefined}
   */
  get backend() {
    return this._asr?.backend;
  }

  /**
   * Loaded model version, or undefined before init.
   * @returns {string|undefined}
   */
  get modelVersion() {
    return this._asr?.modelVersion || this._asr?.version;
  }

  /**
   * Number of samples in the fixed model window.
   * @returns {number}
   */
  get windowSamples() {
    return this._windowSamples;
  }

  /**
   * Number of samples in one rolling stride.
   * @returns {number}
   */
  get strideSamples() {
    return this._strideSamples;
  }

  /**
   * Whether a warmup run is currently in flight.
   *
   * Read immediately after startWarmup, this answers whether it ACCEPTED: the flag is
   * set synchronously on accept, so a caller learns a run began without duplicating
   * the handler's decline conditions — and stays correct as those conditions change.
   * warmLocalAsrForStory spends the once-per-session latch on this rather than on
   * merely having called startWarmup, because a declined call that spent the latch
   * costs the session its only capability measurement, silently.
   * @returns {boolean}
   */
  get isWarmingUp() {
    return this._warmupActive;
  }

  /**
   * Iterations the current (or most recent) warmup run has completed.
   *
   * Exposed so the story launch can tell a run that was measuring but slow from one
   * that never got an iteration in at all. The first is the expected ending of a
   * healthy-but-slow run and belongs on the timeline; the second means the runtime
   * is wedged and belongs in the failure record.
   * @returns {number}
   */
  get warmupIterations() {
    return this._warmupIterations;
  }

  /**
   * Registers a phrase-placement transcript callback.
   * @param {Function} callback
   * @returns {Function}
   */
  onTranscript(callback) {
    return this._registerCallback(this._transcriptCallbacks, callback);
  }

  /**
   * Registers a non-phrase intervention transcript callback.
   * @param {Function} callback
   * @returns {Function}
   */
  onInterventionTranscript(callback) {
    return this._registerCallback(this._interventionTranscriptCallbacks, callback);
  }

  /**
   * Registers a readiness callback.
   * @param {Function} callback
   * @returns {Function}
   */
  onReady(callback) {
    return this._registerCallback(this._readyCallbacks, callback);
  }

  /**
   * Registers an error callback.
   * @param {Function} callback
   * @returns {Function}
   */
  onError(callback) {
    return this._registerCallback(this._errorCallbacks, callback);
  }

  /**
   * Registers a callback for the per-phrase raw transcript log, delivered at endPhrase
   * (see _emitPhraseTranscriptLog).
   * @param {Function} callback
   * @returns {Function}
   */
  onPhraseTranscriptLog(callback) {
    return this._registerCallback(this._phraseTranscriptLogCallbacks, callback);
  }

  /**
   * Loads the ASR runtime and derives fixed streaming dimensions.
   * @returns {Promise<boolean>}
   */
  async init() {
    if (this._ready) {
      return true;
    }

    this._disposed = false;
    this._paused = false;

    try {
      const createOptions = typeof this._createOptions === 'function'
        ? this._createOptions()
        : this._createOptions;
      this._asr = await this._createAsr(createOptions || {});
      this._configureStreamingDimensions();
      this._ready = true;
      this._logInfo('Local ASR initialized', {
        backend: this._asr.backend,
        modelVersion: this._asr.modelVersion || this._asr.version,
        sampleRate: this._sampleRate,
        windowSamples: this._windowSamples,
        strideSamples: this._strideSamples,
        rollingStrideMs: this._asr.decode?.streaming?.rollingStrideMs,
      });
      this._emitReady(true);
      return true;
    } catch (error) {
      const asr = this._asr;
      this._asr = null;
      this._ready = false;

      if (asr && typeof asr.dispose === 'function') {
        try {
          await asr.dispose();
        } catch (disposeError) {
          this._warn('Local ASR init failure cleanup failed', disposeError);
        }
      }

      this._emitReady(false);
      this._emitError({ reason: 'init-failure', error });
      return false;
    }
  }

  /**
   * Audio duration one warmup iteration decodes, in samples — the same unit of work
   * the live decode path does per iteration, which is what makes the timings
   * comparable to `decode.phraseStats`.
   *
   * Streaming advances the recognizer by one STRIDE and hears every sample exactly
   * once, so warmup feeds one stride through streamChunk. Legacy window decode
   * re-decodes a whole window every stride, so there warmup feeds one window through
   * transcribe. Warmup used to time a window under BOTH modes, which measured a code
   * path no streaming session runs (TT-1209).
   * @returns {number}
   * @private
   */
  _warmupAudioSamples() {
    return this._streamDecodeActive() ? this._strideSamples : this._windowSamples;
  }

  /**
   * Wall-clock bound a single warmup iteration must beat for the device to count as
   * fast enough, in the same units as the iteration it is compared against — both
   * now measure one stride's worth of decode work under streaming, one window's
   * worth under legacy window mode.
   *
   * Both bounds derive from the STRIDE duration, by two different routes. Streaming
   * hears each sample once, so its bar is the stride scaled by
   * WARMUP_STREAMING_TARGET_RTF. Window mode re-decodes a whole window per stride, so
   * that decode must finish inside one stride or steady-state windows coalesce; its bar
   * is the bare stride and does NOT scale with the factor.
   *
   * So the branches differ at the current factor — 300ms streaming, 200ms window — and
   * agreed only while it was exactly 1.0. Do not collapse them on either arithmetic.
   *
   * Pre-TT-1179 both modes used the bare stride against a WINDOW-sized inference,
   * which under streaming demanded 5x real time — unsatisfiable at any speed, so
   * capable devices burned the whole budget chasing an exit they could never reach.
   * TT-1179 fixed the bound; TT-1209 fixed the measurement it is compared against.
   * Keep the two modes distinct here if this is ever revisited.
   *
   * The dimension fallback is unreachable today — _configureStreamingDimensions
   * throws unless the sample rate is LOCAL_ASR_SAMPLE_RATE and both dimensions are
   * non-zero, and startWarmup needs _ready, which only init sets. It is kept
   * defensively and derives from the same default the stride itself falls back to.
   * @returns {number}
   */
  _warmupAlreadyFastMs() {
    const strideMs = (this._sampleRate > 0 && this._strideSamples > 0)
      ? (this._strideSamples / this._sampleRate) * 1000
      : DEFAULT_ROLLING_STRIDE_MS;
    return this._streamDecodeActive()
      ? strideMs * WARMUP_STREAMING_TARGET_RTF
      : strideMs;
  }

  /**
   * Measures whether this device can decode fast enough to serve reading locally,
   * and tiers up the WASM JIT while doing it. ONE run per session, bounded by
   * WARMUP_CAP_MS. Returns a promise that settles when the run does, so the caller
   * can hold the pre-story loader until there is a verdict.
   *
   * Runs after the per-story .fst download, not at session init, and decodes through
   * phrase 0's biased HCLG rather than the word-list grammar, so the timings describe the
   * workload real reading runs (TT-1209). It used to fire at init and measure a grammar no
   * phrase uses.
   *
   * `biasedGraph` is a request, not a guarantee — the bytes may not be in the store, the
   * runtime may not support graph loads, or the build may fail. The run resolves that up
   * front and reports the path it ACTUALLY measured, since pooling word-list and
   * biased-graph numbers would corrupt the threshold calibrated from them.
   *
   * The audio is bundled speech-shaped noise (not silence) so the decoder's beam
   * stays populated and the search/lattice paths tier up alongside the acoustic
   * model; a small wordList rides along to warm the beam/trie/n-gram decode path and
   * to give the fallback something to build when there is no graph.
   * @param {{biasedGraph?: object|null, phraseWordCount?: number|null}} [options]
   *   `biasedGraph` is phrase 0's `{ storyId, phraseIndex }` descriptor;
   *   `phraseWordCount` is that phrase's length, reported unchanged so a reader can
   *   tell how big the graph behind these timings was.
   * @returns {Promise<object|undefined>} Settles when the run ends; never rejects.
   *   Resolves with the run's summary — the same object recorded on
   *   runtime.warmupEnded, `verdict` included — which is the read path TT-1169's
   *   routing gate consumes, rather than reaching into `_warmupVerdict`. Resolves
   *   undefined when the call was declined or the run failed unexpectedly, both of
   *   which mean there is no verdict to consume.
   */
  startWarmup({ biasedGraph = null, phraseWordCount = null } = {}) {
    if (!this._ready || !this._asr || this._disposed) {
      return Promise.resolve();
    }
    // A caller awaiting a run already in flight must get THAT run's promise, not a
    // resolved one telling it warmup has finished when it has barely started. Only
    // the module-level once-per-session latch makes this unreachable today, and that
    // guard lives in another file.
    if (this._warmupActive) {
      return this._warmupPromise || Promise.resolve();
    }
    // NEVER START ON TOP OF LIVE AUDIO. cancelWarmup only sets a flag, so a cancel
    // that arrives before the run exists is a no-op — and the reset below would then
    // clear it. That was safe while warmup fired at init, when nothing could be live
    // yet; moving it to story load is what opened the window, and the fire-and-forget
    // props path is where it opens widest: the graph download is unawaited, bounded
    // at 20s of its own, and took 68.1s on the TT-1158 repro device, so phrase 0 is
    // routinely well underway by the time this is reached.
    //
    // Starting anyway is not merely wasteful contention. Warmup's opening chunk sends
    // `reset: true` against phrase 0's own {storyId, phraseIndex} descriptor, so it
    // resolves the recognizer the live phrase owns and _streamChunkNow flushes the
    // utterance and clears wrap.results — the student's committed words, gone
    // mid-phrase. Same failure as the streamFinish collision, arriving through the
    // reset path instead: that guard covers cancel-during-run, this covers
    // start-after-cancel.
    if (this._states[LOCAL_ASR_MODE_PHRASE].active
      || this._states[LOCAL_ASR_MODE_INTERVENTION].active) {
      return Promise.resolve();
    }
    this._warmupActive = true;
    this._warmupCancelled = false;
    this._warmupConverged = false;
    this._warmupVerdict = null;
    this._warmupFirstInferenceMs = 0;
    this._warmupLastInferenceMs = 0;
    this._warmupIterations = 0;
    this._warmupPath = WARMUP_PATH.WORD_LIST;
    const startTime = performance.now();
    const useStreamDecode = this._streamDecodeActive();
    const audioSamples = this._warmupAudioSamples();
    const noise = getWarmupNoise(audioSamples);
    const alreadyFastMs = this._warmupAlreadyFastMs();
    let iterations = 0;

    this._warmupPromise = (async () => {
      // Resolve the biased graph BEFORE the timed loop, and outside it. A build is
      // ~tens of ms on vosk-v3 but is not decode work, and timing it into the first
      // iteration would report staging cost as tier-up cost — the same conflation
      // decode.phraseStats splits out with `coldStages`. The boolean is also the only
      // honest way to label the path: _resolveRecognizer falls back to the word-list
      // grammar silently, so a run that asked for a graph and did not get one would
      // otherwise report biased-graph timings it never measured.
      //
      // Timed, and reported as graphResolveMs, because this span draws down the SAME
      // cap the iterations do — startTime is captured before it. A slow build could
      // therefore produce verdict: BUDGET_EXHAUSTED with iterations: 0 and nothing in
      // the record attributing the loss to staging. With this field
      // `elapsedMs - graphResolveMs` reconciles against the sum of the iterations.
      //
      // startTime is deliberately NOT restarted after the build. The story-launch
      // deadline (LOCAL_ASR_WARMUP_DEADLINE_MS = WARMUP_CAP_MS + 1500) is measured
      // from the same origin, so giving the cap a fresh clock here would let build
      // plus cap overrun it — turning every slow-build run into a deadline cancel,
      // which is a worse outcome than a short measurement.
      const graphOpts = {};
      let graphResolveMs = null;
      if (biasedGraph && typeof this._asr.warmBiasedGraph === 'function') {
        // Spans the queue wait too: the warm is serialized on the engine's decode
        // queue, so time spent waiting for it is time the cap loses either way.
        const graphStartedAt = performance.now();
        let warmed = false;
        try {
          warmed = await this._asr.warmBiasedGraph(biasedGraph);
        } catch (e) {
          warmed = false;
        }
        graphResolveMs = Math.round(performance.now() - graphStartedAt);
        if (warmed) {
          graphOpts.biasedGraph = biasedGraph;
          this._warmupPath = WARMUP_PATH.BIASED_GRAPH;
        }
      }
      const decodeOpts = { ...graphOpts, wordList: WARMUP_WORD_LIST };

      let previousInferenceMs = 0;
      // Run of back-to-back comparisons that stayed inside the convergence
      // threshold; any excursion resets it.
      let stableComparisons = 0;
      // Inference times, newest last, capped at the depth the slow latch needs so
      // recentInferenceMs[0] is always the inference WARMUP_SLOW_LATCH_COMPARISONS
      // comparisons back once the cap is reached.
      const recentInferenceMs = [];
      // Every iteration's wall time for the summary's distribution, unlike
      // recentInferenceMs which forgets past its window depth. The COLD first sample is
      // excluded: it decodes against a just-reset recognizer (27ms against a ~196ms
      // steady state on the device that raised the target factor), so pooling it
      // describes a run the device never had. firstInferenceMs still reports it.
      const steadyInferenceMs = [];

      while (this._warmupActive && !this._warmupCancelled) {
        const elapsed = performance.now() - startTime;
        // A session gets ONE run and this is its hard stop. It is checked BEFORE
        // each iteration rather than after, so the cap bounds when warmup stops
        // starting new work, not when it stops finishing it: an iteration already
        // in flight always runs to completion, so the loader can overrun the cap by
        // up to one iteration on slow hardware. Cutting an inference off mid-decode
        // would neither save the CPU already spent nor yield a usable timing.
        if (elapsed >= WARMUP_CAP_MS) {
          this._warmupNeeded = false;
          this._warmupVerdict = WARMUP_VERDICT.BUDGET_EXHAUSTED;
          break;
        }

        await new Promise(r => setTimeout(r, 0));
        if (this._warmupCancelled) {
          break;
        }

        // Wall time for a single iteration (excludes the setTimeout yield above,
        // and excludes the graph build, which happened before the loop). Safe to
        // call the engine directly (bypassing _processSnapshot) because it
        // serializes all decode work via its internal _tail promise chain.
        //
        // streamChunk under streaming decode, transcribe under window mode: the
        // point of this run is to time the call shape the live path uses, and the
        // two modes use different ones. `reset` on the first chunk clears whatever
        // an earlier use left on the cached recognizer.
        const t0 = performance.now();
        try {
          if (useStreamDecode) {
            await this._asr.streamChunk(noise, { ...decodeOpts, reset: iterations === 0 });
          } else {
            await this._asr.transcribe(noise, decodeOpts);
          }
        } catch (e) {
          // A failing runtime won't be fixed by more warmup.
          this._warmupNeeded = false;
          this._warmupVerdict = WARMUP_VERDICT.ERROR;
          break;
        }
        const inferenceMs = performance.now() - t0;
        iterations++;
        this._warmupIterations = iterations;
        if (iterations === 1) {
          this._warmupFirstInferenceMs = inferenceMs;
        }
        this._warmupLastInferenceMs = inferenceMs;
        if (iterations > 1 && steadyInferenceMs.length < WARMUP_SAMPLE_RETENTION_CAP) {
          steadyInferenceMs.push(inferenceMs);
        }

        const change = previousInferenceMs > 0
          ? Math.abs(inferenceMs - previousInferenceMs) / previousInferenceMs
          : null;
        const stable = change !== null && change <= WARMUP_CONVERGENCE_THRESHOLD;
        stableComparisons = stable ? stableComparisons + 1 : 0;

        recentInferenceMs.push(inferenceMs);
        if (recentInferenceMs.length > WARMUP_SLOW_LATCH_COMPARISONS + 1) {
          recentInferenceMs.shift();
        }
        // Net gain over the last WARMUP_SLOW_LATCH_COMPARISONS comparisons. `change`
        // is unsigned and so cannot tell a plateau from steady improvement; this can.
        //
        // The window is a FIXED DEPTH rather than the whole stable run. A real
        // tier-up curve is a sequence of gains each under the threshold, so the run
        // never resets and spans the decay AND the flat tail that follows it. Anchored
        // at the run's opening inference, the improvement made on the way down keeps
        // reporting forever, so a device that flattens above the bar never latches and
        // burns the entire budget — reinstating the defect this exit removed. Anchoring
        // a fixed number of comparisons back expires that history as it ages out.
        //
        // Only improvement blocks the latch; a device getting SLOWER is definitively
        // slow. The window is full by the time stableComparisons can reach the latch
        // depth, so the two conditions always look at the same run.
        const trendStartMs = recentInferenceMs.length > WARMUP_SLOW_LATCH_COMPARISONS
          ? recentInferenceMs[0]
          : 0;
        const stillImproving = trendStartMs > 0
          && (trendStartMs - inferenceMs) / trendStartMs > WARMUP_CONVERGENCE_THRESHOLD;
        // Side-of-bar is a THREE-way question, not two. Read before the latch below,
        // and pushed into above, so by the time the latch's gates let these be
        // consulted the window is full.
        //
        // A window with samples on both sides is not evidence of anything yet: it is
        // either a device sitting on the bar, or one that crossed it partway through
        // the window and still has a stale opening sample in view. Those need opposite
        // verdicts and this window cannot tell them apart, so neither is latched — see
        // the exit condition.
        const windowUnderBar = recentInferenceMs.every((ms) => ms <= alreadyFastMs);
        const windowOverBar = recentInferenceMs.every((ms) => ms > alreadyFastMs);
        const windowStraddlesBar = !windowUnderBar && !windowOverBar;

        if (this._warmupCancelled) {
          break;
        }

        // Consecutive inferences within +/-15% mean the JIT has tiered up as far as this
        // hardware goes, which settles capability either way — so both outcomes exit, not
        // just the fast one (a plateaued-slow device used to burn the whole budget on a
        // verdict that could no longer change).
        //
        // BOTH branches need WARMUP_SLOW_LATCH_COMPARISONS corroborating comparisons.
        // Exiting fast early is not free: routing depends on this verdict, and a noisy
        // device straddling the bar can put one pair inside the threshold by chance with
        // the latest sample just under it. Stride-scale timings make that likelier than
        // the window-scale ones the single-comparison rule was written for — a 20ms
        // scheduler or GC hiccup is 2.8% of a ~718ms window but 14% of a ~140ms stride,
        // right at the threshold. The extra comparison now costs about one stride, paid
        // behind a loader the student already waits through.
        //
        // The trend gate stays slow-only: it exists to stop a device still tiering down
        // from being called slow before it arrives, and a device already under the bar
        // has arrived.
        //
        // Corroboration and consensus answer different questions and a verdict needs
        // both. Corroboration says the timings are stable, not which side of the bar they
        // are stable on; judging that from the latest sample alone made a straddling
        // device a coin flip (205/200/199 latched FAST, 199/200/205 latched SLOW on the
        // same hardware).
        //
        // A STRADDLING WINDOW LATCHES NOTHING. Calling it slow looked conservative but
        // punished the commonest shape — a tier-up curve crossing the bar mid-window.
        // 210/195/185 nets 11.9%, so the trend gate sees no improvement, yet its last two
        // strides are already under the bar. Iterating ages the stale opening sample out
        // and lets the window resolve on evidence rather than sample order. A device that
        // genuinely sits ON the bar never assembles a unanimous window, so it is never
        // admitted to local decode — it runs to the cap and reports BUDGET_EXHAUSTED,
        // which costs the loader the full cap in that rare case and is the price of not
        // guessing.
        if (iterations >= WARMUP_MIN_ITERATIONS
          && stable
          && stableComparisons >= WARMUP_SLOW_LATCH_COMPARISONS
          && !windowStraddlesBar
          && (windowUnderBar || !stillImproving)) {
          this._warmupConverged = windowUnderBar;
          this._warmupVerdict = windowUnderBar ? WARMUP_VERDICT.FAST : WARMUP_VERDICT.SLOW;
          this._warmupNeeded = false;
          break;
        }

        previousInferenceMs = inferenceMs;
      }
      this._warmupActive = false;
      // Snapshotted here, before the streamFinish await below, and used for BOTH the
      // release guard and the summary. streamFinish is serialized on the engine's
      // queue and can take a while; a deadline cancel landing inside it would
      // otherwise flip the flag after the run had already reached its verdict, and
      // publish `cancelled: true` beside `verdict: 'fast'` — a row that reads as a
      // contradiction. What the summary should say is whether the run was cancelled
      // BEFORE it finished deciding, which is what this captures.
      const cancelled = this._warmupCancelled;
      // Release the streaming slot the warmup chunks claimed, but ONLY for a run that
      // ended on its own terms — a verdict, the cap, or an inference error. Without
      // the release the recognizer stays marked as owned by a phrase that never
      // existed, which _evictLru then passes over, and phrase 0's opening chunk reads
      // as a clobber: spending the once-per-session clobber warning on warmup and
      // hiding a real one later.
      //
      // A CANCELLED run must NOT release, because cancellation means live audio is
      // already arriving. Phrase 0's opening chunk enqueues on the engine's _tail
      // behind warmup's last in-flight chunk, so a streamFinish queued here lands
      // after it — and decodeOpts carries the same {storyId, phraseIndex: 0} key the
      // live phrase resolves to, so _findCachedRecognizer hands back the recognizer
      // that phrase now owns. Flushing it retrieves a final result, resetting Kaldi's
      // utterance mid-phrase, and then clears wrap.results: the words the student read
      // in the opening seconds of phrase 0, gone.
      //
      // The abandoned claim is the cheaper price. Per _adoptStreamingWrap it is not a
      // transcript risk — the next phrase's reset flush clears the recognizer either
      // way — and on the mainline path warmup and phrase 0 share one wrap, so no
      // clobber fires to spend the latch anyway.
      //
      // Cache-hit only and never rejects, so it cannot build anything or fail the run;
      // phrase 0 still passes `reset` on its first chunk regardless.
      if (useStreamDecode
        && iterations > 0
        && !cancelled
        && typeof this._asr.streamFinish === 'function') {
        try {
          await this._asr.streamFinish(decodeOpts);
        } catch (e) {
          // Nothing to salvage: the transcript is noise and the claim is released
          // by the next reset either way.
        }
      }
      const elapsedMs = Math.round(performance.now() - startTime);
      // first / last, so above 1.0 means the runtime got FASTER and below 1.0
      // means it degraded. Reported this way round, and renamed from `speedup`,
      // because the previous last / first orientation made a slowdown read as a
      // speedup: the TT-1158 Chromebook logged 1.6 while actually ending 1.6x
      // slower than it started (3770ms to 6037ms). Renaming rather than quietly
      // inverting keeps pre-fix S3 records distinguishable from post-fix ones, so
      // TT-1179's threshold calibration cannot average the two orientations
      // together.
      //
      // BOTH endpoints must be positive. Guarding on the denominator alone would
      // report 0 — an infinite slowdown — for a run whose first inference timed
      // as 0ms, which is a plausible reading under a coarsened performance.now
      // and would land in the same S3 field the threshold calibration reads.
      //
      // There is no `tierUpFactorOverall` any more. It aggregated across re-armed
      // runs; a session has one run now, so it would only ever have duplicated this.
      const tierUpFactor = ratioOrNull(this._warmupFirstInferenceMs, this._warmupLastInferenceMs);

      const audioMsPerIteration = (audioSamples > 0 && this._sampleRate > 0)
        ? Math.round((audioSamples / this._sampleRate) * 1000)
        : null;

      // Sorted once for every statistic drawn from it below.
      const sortedSteadyMs = [...steadyInferenceMs].sort((a, b) => a - b);
      const medianInferenceMs = percentileOfSorted(sortedSteadyMs, 0.5);
      const meanInferenceMs = sortedSteadyMs.length
        ? Math.round(sortedSteadyMs.reduce((total, ms) => total + ms, 0) / sortedSteadyMs.length)
        : null;

      const summary = {
        cancelled,
        converged: this._warmupConverged,
        // A cancelled run reaches no settled verdict of its own; say so rather
        // than leaving it null, so a consumer cannot read it as a failure.
        verdict: this._warmupVerdict
          || (cancelled ? WARMUP_VERDICT.CANCELLED : null),
        iterations,
        elapsedMs,
        firstInferenceMs: Math.round(this._warmupFirstInferenceMs || 0),
        lastInferenceMs: Math.round(this._warmupLastInferenceMs || 0),
        tierUpFactor,
        // Which decode path was STAGED. `biasedGraph` is the per-phrase HCLG real
        // reading uses; `wordListGrammar` means phrase 0's graph was not available and
        // any timings here describe a different workload, which no threshold
        // calibration may pool with the other.
        //
        // Staged, not necessarily measured: it is set the moment warmBiasedGraph
        // resolves true, before the first iteration, so a run cancelled before its
        // opening chunk publishes path: 'biasedGraph' with iterations: 0 and no
        // timings. That is deliberate — the staging did happen and is worth recording
        // — and it is recoverable, since iterations and realTimeFactor disambiguate.
        // A CONSUMER FILTERING FOR CALIBRATION MUST REQUIRE realTimeFactor TO BE
        // PRESENT, not merely path === 'biasedGraph'.
        path: this._warmupPath,
        // Length of the phrase whose graph was measured. HCLG decode cost scales with
        // the graph, so this says how representative the number below is of the rest
        // of the story. Null when the caller did not supply it or the path fell back
        // to the word-list grammar, where no phrase graph was involved.
        phraseWordCount: this._warmupPath === WARMUP_PATH.BIASED_GRAPH
          ? phraseWordCount
          : null,
        // How much of WARMUP_CAP_MS the graph build (plus its queue wait) consumed
        // before any audio was decoded, so a short or empty run can be attributed.
        // Null when no graph was requested, or the engine cannot warm one.
        graphResolveMs,
        // The bar the verdict was reached against, and the decode mode that
        // determined it — without both, a logged verdict cannot be re-derived
        // from the timings beside it.
        //
        // Logged at the precision it is COMPARED at, not rounded. At the current
        // factor of 1.5 the streaming bar is exactly 300 and rounding would be
        // harmless — but it was not at 0.7, where the bar was 717.5ms and rounding
        // published 718 beside a 718ms inference that had just lost the comparison,
        // a verdict reading as its own contradiction in the one input whose precision
        // is ours to choose. The factor can be lowered again, so the precision is kept
        // rather than reintroduced later; toFixed rather than the raw value because a
        // fractional bar comes out of the float arithmetic as e.g. 717.5000000000001.
        alreadyFastMs: +alreadyFastMs.toFixed(2),
        decodeMode: useStreamDecode ? 'streaming' : 'window',
        // Audio decoded per iteration: one stride under streaming, one window under
        // window mode. This is the denominator of realTimeFactor below, and it is
        // what makes a row self-describing — the same field name meant ~1025ms of
        // audio before TT-1209 and means ~200ms after it under streaming, so without
        // it the two eras are indistinguishable in S3 and averageable into nonsense.
        audioMsPerIteration,
        // Decode wall time per unit of audio. Deliberately the same name and the same
        // meaning as decode.phraseStats.realTimeFactor, because they are now directly
        // comparable: both measure one stride's decode against one stride of audio
        // under streaming. It was `windowRealTimeFactor` while warmup timed a window
        // and phraseStats timed a stride advance; the rename marks that boundary.
        //
        // Same UNIT, but not yet a like-for-like number. This run feeds three to five
        // strides into a single utterance (reset only on iteration 0) and stops, so it
        // describes the OPENING strides of an utterance, where the lattice is at its
        // shallowest. phraseStats averages across whole phrases, lattice depth and all.
        // Expect warmup to read systematically optimistic against it; TT-1179 should
        // measure the offset rather than assume it away, since it calibrates off this
        // field. (The synthetic-noise input, noted above, cuts the same way.)
        realTimeFactor: (audioMsPerIteration && this._warmupLastInferenceMs > 0)
          ? +(this._warmupLastInferenceMs / audioMsPerIteration).toFixed(2)
          : null,
        // STEADY-STATE DISTRIBUTION: the run, not two samples of it, cold sample
        // excluded. A BUDGET_EXHAUSTED verdict is unreadable without it — the device
        // that raised the target factor sat at a ~196ms median against a 200ms bar over
        // 29 iterations, none of it visible from its comfortable-looking 194ms last
        // sample. steadySamples also says which way the run's own bias points, since a
        // long run feeds tens of strides into ONE utterance and so builds a lattice
        // deeper than any real phrase: optimistic when it latches early, pessimistic
        // when it straddles.
        steadySamples: sortedSteadyMs.length,
        meanInferenceMs,
        medianInferenceMs,
        p75InferenceMs: percentileOfSorted(sortedSteadyMs, 0.75),
        maxInferenceMs: sortedSteadyMs.length
          ? Math.round(sortedSteadyMs[sortedSteadyMs.length - 1])
          : null,
        // 0 or steadySamples is a unanimous run; between them is a straddler, and the
        // ratio says how squarely it sits on the bar rather than near it.
        samplesOverBar: sortedSteadyMs.filter((ms) => Math.round(ms) > alreadyFastMs).length,
        // Robust counterpart to realTimeFactor, which reads the last sample alone and so
        // swings with whichever side of the bar the run stopped on. Prefer this for
        // calibration.
        steadyRealTimeFactor: ratioOrNull(medianInferenceMs, audioMsPerIteration),
      };
      this._logInfo('Local ASR warmup ended', summary);
      // Also recorded to the timeline because _logInfo is console-only: the warmup verdict
      // is the earliest signal that a device cannot keep pace, and it was reaching S3 for
      // nobody — a field report arrived with the decisive number visible only in a
      // browser console export.
      recordLocalAsrLifecycle('runtime.warmupEnded', summary);
      // The promise's resolution value IS the verdict's read path (TT-1169): the
      // routing gate consumes this rather than a getter into private state, so a
      // consumer can never observe a verdict before the run that owns it has
      // finished summarising.
      return summary;
    })().catch((error) => {
      this._warmupActive = false;
      this._warn('Local ASR warmup unexpected failure', error);
    });
    return this._warmupPromise;
  }

  /**
   * Signals the warmup loop to exit after the current in-flight iteration — live
   * audio is (about to be) flowing, so warmup must stay out of the decode queue.
   *
   * Warmup settles behind the pre-story loader now, before any phrase can start, so
   * this is a guard against the cap expiring into a launch rather than the routine
   * interruption it was when warmup re-armed in every phrase gap. A cancelled run
   * reports WARMUP_VERDICT.CANCELLED and reaches no capability verdict.
   */
  cancelWarmup() {
    this._warmupCancelled = true;
  }

  /**
   * Starts a phrase-scoped local placement lifecycle.
   * Synchronous by contract: Speech.enableRecording calls this and then starts the
   * recorder without awaiting, so any suspension here arms the phrase AFTER audio is
   * already flowing and the new phrase's opening strides fail the identity check in
   * _canUseStateForAudio.
   * @param {object} phraseMeta
   * @returns {boolean} true when the phrase is eligible for local ASR.
   */
  startPhrase(phraseMeta = {}) {
    if (!this._ready) {
      this._warn('Local ASR startPhrase called before init', phraseMeta);
      return false;
    }

    this.cancelWarmup();

    const metadata = {
      ...phraseMeta,
      mode: phraseMeta.mode || LOCAL_ASR_MODE_PHRASE,
    };
    const eligible = this._isLocalPlacementEligible({
      locale: getLocale(metadata),
    });

    this._resetState(this._states[LOCAL_ASR_MODE_PHRASE], metadata, eligible);
    this._lastPhraseUsedLocalAsr = Boolean(eligible);
    if (eligible) {
      this._warmBiasedGraphFor(metadata);
    }
    this._logInfo('Local ASR phrase started', {
      activityId: metadata.activityId,
      phraseIndex: metadata.phraseIndex,
      eligible: Boolean(eligible),
      languageCode: metadata.languageCode,
      placementMode: getPlacementMode(metadata),
      wordCount: getWordList(metadata).length,
    });
    return eligible;
  }

  /**
   * Whether the active backend can consume per-phrase biased graphs (vosk marks
   * itself with `supportsBiasedGraphs`; NeMo does not). Gates descriptor threading so
   * incapable backends see byte-identical transcribe opts.
   * @returns {boolean}
   * @private
   */
  _supportsBiasedGraph() {
    return Boolean(this._asr && this._asr.supportsBiasedGraphs);
  }

  /**
   * Best-effort warm of one phrase's biased-graph recognizer, so its first decode
   * window is a cache hit instead of paying the create inline on the serialized
   * decode queue. Called with the CURRENT phrase at startPhrase — safe on v3
   * because the create is ~tens of ms and the first stride's ~200 ms of audio
   * runway hides it (v2's seconds-long AM re-parse made this exact call site a
   * measured dead cursor, which is why the old warm machinery needed its endPhrase
   * scheduling) — and with the NEXT phrase (`phraseIndex + 1`) at endPhrase, using
   * the inter-phrase break as extra runway for hardware where the create exceeds a
   * stride. Insurance, not a dependency: a missed warm (store bytes not landed,
   * skipped phrase, story end) just means the live path builds inline. `offset`
   * past the end of the story is a store miss the warm quietly declines.
   * @param {object} metadata Phrase metadata carrying storyId/phraseIndex.
   * @param {number} [offset] 0 for the current phrase, 1 for the next.
   * @returns {void}
   * @private
   */
  _warmBiasedGraphFor(metadata, offset = 0) {
    if (!this._supportsBiasedGraph() || typeof this._asr.warmBiasedGraph !== 'function') {
      return;
    }
    const current = getBiasedGraphDescriptor(metadata);
    if (!current) {
      return;
    }
    // Best-effort and never rejects, so it is safe to leave unawaited; it runs on
    // the ASR's serialized decode queue. `speculative` marks the next-phrase warm
    // (offset > 0): its target may never be read, so a build failure there is
    // labeled `warm: true` in telemetry — the current-phrase warm's failure is a
    // real degradation of the phrase being read and reports unlabeled.
    this._asr.warmBiasedGraph(
      offset === 0 ? current : { ...current, phraseIndex: current.phraseIndex + offset },
      { speculative: offset > 0 },
    );
  }

  /**
   * Awaited readiness check for one phrase's biased graph, used by ReaderManager's
   * end-of-phrase preparation (TT-1283) to decide whether the phrase decodes locally or
   * must route to server Kaldi. Resolves the phrase's recognizer through the SAME
   * `warmBiasedGraph` the speculative warm uses, so this doubles as the load-bearing warm
   * — but it is awaited and its boolean is authoritative.
   *
   * Returns true when the phrase WILL decode on a real biased graph (bytes are in the
   * store and the recognizer built/cached), or when the backend has no biased-graph path
   * at all (NeMo) and therefore no defective word-list grammar to route around. Returns
   * false only for a Vosk phrase that would fall back to the word-list grammar (graph
   * never downloaded, FST build failure, or a runtime that cannot bind a graph) — the
   * caller then routes that phrase to server ASR instead of the broken word-list decode.
   *
   * Never warms a graph that is not present: `warmBiasedGraph` returns false on a store
   * miss without building, so a missing-graph phrase costs nothing here.
   *
   * `speculative` is forwarded to the warm so its telemetry is labeled correctly: the
   * end-of-phrase prep targets a NEXT phrase the student may never read (speculative:
   * true), while the story-launch prep targets phrase 0 which WILL be read (false). A
   * build failure on a speculative target must not report as a real degradation of a
   * phrase being read. Note the handler already speculatively warms `phraseIndex + 1` at
   * endPhrase; when this runs for the same descriptor it is a cache hit.
   *
   * @param {object} metadata Phrase metadata carrying storyId/phraseIndex.
   * @param {{speculative?: boolean}} [options]
   * @returns {Promise<boolean>} true = decode local biased (or no biased path); false = route to server.
   */
  async prepareBiasedGraphForPhrase(metadata, { speculative = false } = {}) {
    if (!this._supportsBiasedGraph() || typeof this._asr.warmBiasedGraph !== 'function') {
      // Backends without a biased-graph path (NeMo) never use the defective word-list
      // grammar, so there is nothing to route around — keep the phrase local.
      return true;
    }
    const descriptor = getBiasedGraphDescriptor(metadata);
    if (!descriptor) {
      // No resolvable descriptor means the phrase cannot decode biased; on Vosk that is
      // the word-list grammar, so route to server.
      return false;
    }
    try {
      return Boolean(await this._asr.warmBiasedGraph(descriptor, { speculative }));
    } catch (err) {
      this._warn('prepareBiasedGraphForPhrase warm failed', { error: err?.message });
      return false;
    }
  }

  /**
   * Whether streaming decode owns audio for this session (flag on, backend exposes
   * streamChunk — Vosk only). Session-constant and mode-independent: BOTH phrases and
   * interventions stream, and this deliberately does not consult whether a phrase is
   * active, so routing built on it cannot depend on lifecycle timing.
   *
   * What streaming buys an intervention is decode economy: window decode re-decoded a whole
   * 1025ms window every 200ms stride, streaming hears each sample once (~5x less acoustic
   * compute). It also reaches a first result sooner, but that is bounded upside, not a fix —
   * capture is continuous so a short answer was never lost, and Listen.handleMatches will
   * not resolve before its >=1s minTimeout either way. See
   * docs/tt-828-client-asr-audit-findings.md domain facts #1 and #3.
   *
   * The cost is minSightings' smoothing, which mattered because Listen recomputes its match
   * per emission; handled at the emit, see pickInterventionToken.
   * @returns {boolean}
   * @private
   */
  _streamDecodeActive() {
    return this._streamDecode && Boolean(this._asr && typeof this._asr.streamChunk === 'function');
  }


  /**
   * Resolves the biased-HCLG descriptor for a capture, or null.
   *
   * A streaming intervention gets the phrase's descriptor plus a scope segment
   * (BIASED_GRAPH_SCOPE_INTERVENTION). The scope changes only the engine's CACHE KEY —
   * byte resolution still goes through getStoryGraphBytes(storyId, phraseIndex) — so the
   * phrase and its interventions share one download and one parse while streaming on
   * separate recognizers, which is what keeps an intervention's reset/flush off the
   * recognizer the phrase is mid-utterance on. Scoped per phrase, not per target, so
   * repeat interventions in a phrase are cache hits.
   *
   * Window-mode interventions keep the UNSCOPED key: there retrieveFinalResult resets
   * utterance state every window anyway, so sharing is safe and skips a create.
   *
   * Gated on the session-constant streaming flag, not the phrase's live `active` flag, so
   * routing is invariant rather than timing-dependent.
   * @param {object} state
   * @returns {object|null}
   * @private
   */
  _captureBiasedGraph(state) {
    if (!this._supportsBiasedGraph()) {
      return null;
    }
    const descriptor = getBiasedGraphDescriptor(state.metadata);
    if (!descriptor) {
      return null;
    }
    if (state.mode === LOCAL_ASR_MODE_INTERVENTION && this._streamDecodeActive()) {
      return { ...descriptor, scope: BIASED_GRAPH_SCOPE_INTERVENTION };
    }
    return descriptor;
  }

  /**
   * Starts or resets an isolated intervention transcript lifecycle.
   * @param {object} interventionMeta
   * @returns {boolean}
   */
  startIntervention(interventionMeta = {}) {
    if (!this._ready) {
      this._warn('Local ASR startIntervention called before init', interventionMeta);
      return false;
    }

    this.cancelWarmup();

    if (getWordList(interventionMeta).length === 0) {
      this._warnInterventionStartFailure(
        'missing-target-text',
        'Local ASR intervention requires expected text or wordList',
        interventionMeta,
      );
      return false;
    }

    if (!this._shouldUseLocalAsrForIntervention(interventionMeta)) {
      this._warnInterventionStartFailure(
        'placement-not-local',
        'Local ASR intervention skipped because placement is not using local ASR',
        interventionMeta,
      );
      return false;
    }

    const metadata = {
      ...interventionMeta,
      mode: LOCAL_ASR_MODE_INTERVENTION,
    };
    this._resetState(this._states[LOCAL_ASR_MODE_INTERVENTION], metadata, true);
    this._lastInterventionStartWarningKey = null;
    // Deliberately NO warm of the intervention-scoped recognizer here. This is
    // only reached lazily from _getState on the first intervention audio chunk
    // (nothing calls startIntervention earlier), so a warm enqueued here lands in
    // the worker FIFO at the same position as the inline create it would
    // front-run — and the create is absorbed by the leading-silence strides
    // before the student says the target word regardless. A warm that actually
    // helps needs to run during the intervention's PREAMBLE, i.e. an explicit
    // startIntervention call from Speech.requestTranscript before listening
    // starts; if field telemetry ever shows intervention create stalls
    // (graphScope-labeled buildFailed fallbacks), that is the fix to reach for.
    return true;
  }

  /**
   * Accepts 16kHz mono Float32 PCM samples for phrase or intervention ASR.
   * @param {object} payload
   * @param {Float32Array} payload.samples16k
   * @param {string} [payload.mode]
   * @param {boolean} [payload.done]
   * @returns {Promise<void>}
   */
  async audioSamples({
    samples16k,
    mode = LOCAL_ASR_MODE_PHRASE,
    done = false,
    ...identity
  } = {}) {
    const canAccept = this._canAcceptAudio();
    const state = canAccept ? this._getState(mode, identity) : null;
    const usable = Boolean(state) && this._canUseStateForAudio(state, identity);

    if (!usable) {
      return;
    }

    const samples = samples16k instanceof Float32Array
      ? samples16k
      : new Float32Array(samples16k || []);
    const work = [];

    if (samples.length > 0) {
      work.push(...this._appendSamples(state, samples));
    }

    if (done) {
      const doneWork = this._flushPartialStride(state);
      if (doneWork) {
        work.push(doneWork);
      }
    }

    await Promise.all(work);
  }

  /**
   * Parses a server WAV Blob bridge path into 16kHz samples.
   * @param {object} payload
   * @param {Blob} payload.blob
   * @returns {Promise<void>}
   */
  async audioSegment({ blob, ...rest } = {}) {
    if (!blob) {
      return;
    }

    // Snapshotted before the await: endIntervention/endPhrase can land inside it, and
    // audioSamples would then LAZILY RE-ARM from this stale chunk's metadata — leaving a
    // stray armed intervention that the next one appends to with reset:false, which is the
    // channel endIntervention exists to close. The samples path is immune (it reaches
    // _getState synchronously). Effectively unreachable today — this branch needs a
    // non-silent segment whose samples16k is null, and wav-worker derives that from the same
    // buffer encodeWAV just consumed — but it is cheap insurance on a failure that has
    // recurred.
    const mode = rest.mode || LOCAL_ASR_MODE_PHRASE;
    const generationBefore = this._states[mode]?.generation;
    try {
      const samples16k = await wavBlobToFloat32(blob);
      if (this._states[mode]?.generation !== generationBefore) {
        return undefined;
      }
      return this.audioSamples({ ...rest, samples16k });
    } catch (error) {
      this._warn('Local ASR audio segment parse failed; dropping chunk', {
        mode: rest.mode || LOCAL_ASR_MODE_PHRASE,
        error,
      });
    }
  }

  /**
   * Drops incoming audio while preserving active phrase/intervention state.
   * @returns {void}
   */
  pause() {
    this._paused = true;
  }

  /**
   * Resumes accepting audio into the existing active state.
   * @returns {void}
   */
  resume() {
    this._paused = false;
  }

  /**
   * Clears the accumulated phrase transcript WITHOUT ending the phrase lifecycle.
   *
   * Exists because a transcript reset and a phrase ending are different events that
   * used to share one code path. Interventions that run mid-phrase (the clap/
   * RepeatAfterMe listen hardcodes `endOfPhrase: true`, which in Listen means
   * "wipe the transcript rather than restore it") reset the transcript while the
   * student is still reading the phrase. Routing those through endPhrase tore the
   * phrase state down with nothing left to re-arm it — startPhrase only runs from
   * enableRecording at a phrase boundary — so every later audio stride was dropped
   * at the acceptance gate and the reader's interim transcript stayed null forever.
   *
   * Clearing emittedText is what lets the next decode re-emit: the reader's
   * transcript was just wiped, and the emit dedupe would otherwise suppress the
   * cumulative text as unchanged, leaving the reader with nothing.
   * @returns {void}
   */
  resetPhraseTranscript() {
    const state = this._states[LOCAL_ASR_MODE_PHRASE];

    // Normally Speech.cancelTranscriptRequest has already ended the intervention; this
    // covers a transcript wipe that arrives with one still armed. Routed through
    // endIntervention so the flush, the stats flush and the clear cannot diverge across
    // the three teardown sites.
    this.endIntervention();

    if (!state.active) {
      return;
    }

    state.emittedText = '';
    state.lastLoggedRawText = null;
  }

  /**
   * Best-effort flush of a state's streaming recognizer at teardown, so a recognizer
   * reused later holds no stale committed segments. Callers: endIntervention (which all
   * intervention teardown paths route through) and endPhrase.
   *
   * Mostly belt-and-braces — the next use sends `reset: true` — but it leaves the
   * recognizer clean even when no further intervention runs on that phrase.
   *
   * Gated on DISPATCH, not on a landed result: a lifecycle can end with its opening decode
   * still queued, and gating on streamStarted skipped the finish for exactly those. The
   * captured descriptor (scoped for interventions) routes the flush to the recognizer this
   * state's decodes ran on; the engine flushes only what is already cached.
   * @param {object} state
   * @returns {void}
   * @private
   */
  _streamFinishState(state) {
    if (!state.streamDispatched || !this._asr || typeof this._asr.streamFinish !== 'function') {
      return;
    }
    const streamOpts = { wordList: getWordList(state.metadata) };
    if (state.mode === LOCAL_ASR_MODE_INTERVENTION) {
      // Without the slot the fallback lookup resolves the UNSCOPED grammar key — the
      // phrase's own recognizer — and flushes its committed segments instead.
      streamOpts.streamSlot = INTERVENTION_STREAM_SLOT;
    }
    const streamGraph = this._captureBiasedGraph(state);
    if (streamGraph) {
      streamOpts.biasedGraph = streamGraph;
    }
    this._asr.streamFinish(streamOpts).catch(() => {});
  }

  /**
   * Ends the intervention lifecycle: flushes its recognizer, reports its decode stats and
   * clears the state so the next intervention arms fresh.
   *
   * Told, not inferred, because the handler has no way to infer it: intervention audio
   * carries the PHRASE's recording props, so consecutive interventions in one phrase share
   * `phraseIndex`, and the judged word is no better a discriminator (a repeated phrase
   * word, or Listen's silence retry, gives two interventions the same target). Under
   * streaming the recognizer holds an open utterance between chunks, so without this the
   * next intervention decoded as a continuation of the previous one and reused its
   * `emittedText`. Window decode hid that by resetting utterance state every window.
   *
   * Two call sites, because interventions have two kinds of exit and reviewers have twice
   * read one as covering both:
   *   - Speech.cancelTranscriptRequest — every Listen resolve (handleMatches) and abort
   *     (abortListen), which is also how comprehension's UNMOUNT cleanup exits.
   *   - Speech.disableRecording — comprehension's normal COMPLETION (triggerInference), which
   *     never reaches cancelTranscriptRequest.
   * Idempotent, so wiring both is free and "armed iff Speech thinks a listen is live" holds
   * without case analysis.
   * @returns {void}
   */
  endIntervention() {
    const state = this._states[LOCAL_ASR_MODE_INTERVENTION];
    if (!state.active) {
      return;
    }
    // Up to one stride (~200ms) of trailing audio is dropped here: intervention payloads
    // never carry done:true (Speech forces done=false for the life of a transcriptRequest),
    // so _flushPartialStride never runs and strideBuffer goes with _clearState. Usually room
    // tone given continuous capture. streamFinish's own transcript is discarded too, and by
    // this point Listen has resolved and nulled transcriptRequest, so there is no consumer
    // left to emit it to.
    //
    // Both read state.metadata, which _clearState wipes.
    this._streamFinishState(state);
    this._countBoundaryDiscard(state);
    this._finalizeDecodeStats(state, 'ended');
    this._clearState(state);
  }

  /**
   * Ends the phrase lifecycle without starting new transcribe work by default.
   * @param {object} [options]
   * @param {boolean} [options.emitFinal=false] Emits the consensus's finalized text.
   * Window mode only: streaming decode bypasses consensus, so there is no accumulated
   * consensus text and this is a no-op there — the cumulative transcript was already
   * emitted per chunk, and streamFinish's trailing text is deliberately dropped
   * (endPhrase runs at transcript-reset points, so a late emission would land in the
   * NEXT phrase's interim transcript).
   * Synchronous by contract: resetPlacementTranscript and endUniversalSocketSession call
   * this without awaiting, so suspending here would let the next phrase arm (or the
   * activity flush run) against half-torn-down state.
   * @returns {void}
   */
  endPhrase({ emitFinal = false } = {}) {
    const state = this._states[LOCAL_ASR_MODE_PHRASE];

    if (state.active) {
      if (state.consensus) {
        const final = state.consensus.finalize();
        if (emitFinal) {
          this._emitTranscript(state, final.text);
        }
      }
      // Flush so a recognizer reused later starts clean and holds no stale
      // committed segments (see _streamFinishState for the dispatch gating).
      this._streamFinishState(state);
      // Under streaming decode a queue drop is
      // audio the decoder NEVER heard (strides shed from the coalesced backlog once it
      // passes STREAM_BACKLOG_CAP_SAMPLES); in window mode it is a superseded rolling
      // window, which is benign — the same audio is covered by the next one. Drops used
      // to be logged alone, and console-only: they said audio was shed without saying
      // whether the decoder or the graph staging shed it, and never reached S3, so a
      // field report could not be diagnosed at all. _reportDecodeStats carries both.
      // saveSessionAudio fires audio without awaiting, so the phrase's last stride can
      // still be in the worker here; _finalizeDecodeStats hands the report to that decode
      // rather than blocking the phrase turnover on it.
      this._countBoundaryDiscard(state);
      this._finalizeDecodeStats(state, 'ended');
      // Reads state that _clearState wipes, so it runs before it.
      this._emitPhraseTranscriptLog(state);
      // The inter-phrase break starts here: warm the next phrase's recognizer so
      // even hardware where the (cheap) v3 create exceeds one stride starts the
      // next phrase on a cache hit. Sequential-reading guess — a skipped phrase
      // is a store miss covered by startPhrase's current-phrase warm. No re-warm
      // latch needed: _clearState (next line) drops eligibility, so a second
      // endPhrase cannot re-enter this branch.
      if (state.eligible) {
        this._warmBiasedGraphFor(state.metadata, 1);
      }
      this._clearState(state);
    }

    // An end-of-phrase intervention still armed at the boundary: same teardown, so its
    // recognizer cannot carry an utterance into the next phrase's interventions.
    this.endIntervention();

    // No warmup re-arm here any more. It existed because warmup fired at session
    // init, where phrase 0 could arm moments later and cancel it with zero
    // iterations; re-arming in idle gaps recovered the tier-up that lost. Warmup now
    // runs to a verdict behind the pre-story loader before any phrase can start
    // (TT-1209), so there is nothing to recover, and the gap re-arms only put noise
    // decodes back on the queue the student's audio is waiting for. Long stories
    // therefore no longer get their JIT re-poked mid-read, which is fine once the
    // runtime has tiered — and by the time the first phrase begins, it has.
  }

  /**
   * Releases the ASR runtime and invalidates pending asynchronous results.
   * Best-effort drain of in-flight warmup, bounded by WARMUP_DISPOSE_DRAIN_MS.
   * Nothing can start a warmup after teardown: there is no re-arm path any more,
   * and startWarmup returns early on _disposed.
   * @returns {Promise<void>}
   */
  async dispose() {
    this._disposed = true;
    this.cancelWarmup();
    if (this._warmupPromise) {
      let id;
      const timeout = new Promise(r => { id = setTimeout(r, WARMUP_DISPOSE_DRAIN_MS); });
      try {
        await Promise.race([this._warmupPromise, timeout]);
      } finally {
        clearTimeout(id);
      }
      this._warmupPromise = null;
    }

    this._ready = false;
    this._paused = true;
    this._lastPhraseUsedLocalAsr = false;

    const pendingWork = Object.values(this._states)
      .map((state) => state.processingPromise)
      .filter(Boolean);

    // Settle before reporting so a teardown mid-decode still carries that decode's cost.
    // Safe to await HERE (unlike on the phrase path): dispose is already async, and no
    // audio is routed to this handler after _disposed latches.
    if (pendingWork.length > 0) {
      await Promise.allSettled(pendingWork);
    }

    Object.values(this._states).forEach((state) => {
      if (state.active) {
        this._finalizeDecodeStats(state, 'tornDown');
      }
      this._clearState(state);
    });

    const asr = this._asr;

    // Keep the runtime attached until pending drains settle; a drain may still
    // need the current ASR reference before disposal invalidates it.
    this._asr = null;
    if (asr && typeof asr.dispose === 'function') {
      await asr.dispose();
    }

    this._transcriptCallbacks.clear();
    this._interventionTranscriptCallbacks.clear();
    this._readyCallbacks.clear();
    this._errorCallbacks.clear();
    this._phraseTranscriptLogCallbacks.clear();
  }

  /**
   * Registers a callback in a Set and returns an unsubscribe function.
   * @param {Set<Function>} callbacks
   * @param {Function} callback
   * @returns {Function}
   * @private
   */
  _registerCallback(callbacks, callback) {
    if (typeof callback !== 'function') {
      return () => {};
    }
    callbacks.add(callback);
    return () => callbacks.delete(callback);
  }

  /**
   * Derives sample dimensions from the ASR instance.
   * @returns {void}
   * @private
   */
  _configureStreamingDimensions() {
    this._sampleRate = this._asr.sampleRate || LOCAL_ASR_SAMPLE_RATE;
    if (this._sampleRate !== LOCAL_ASR_SAMPLE_RATE) {
      throw new Error(`Local ASR requires ${LOCAL_ASR_SAMPLE_RATE}Hz audio, got ${this._sampleRate}Hz`);
    }

    this._windowSamples = this._asr._engine?.windowSamples
      || Math.round((this._asr.windowMs || DEFAULT_WINDOW_MS) * this._sampleRate / 1000);

    const rollingStrideMs = this._asr.decode?.streaming?.rollingStrideMs || DEFAULT_ROLLING_STRIDE_MS;
    this._strideSamples = Math.round(this._sampleRate * rollingStrideMs / 1000);

    if (!this._windowSamples || !this._strideSamples || this._strideSamples > this._windowSamples) {
      throw new Error('Local ASR streaming dimensions are invalid');
    }
  }

  /**
   * Resets one mode to an active lifecycle.
   * @param {object} state
   * @param {object} metadata
   * @param {boolean} eligible
   * @returns {void}
   * @private
   */
  _resetState(state, metadata, eligible) {
    if (state.active) {
      this._finalizeDecodeStats(state, 'superseded');
    }
    this._activateState(state, metadata, eligible);
  }

  /**
   * Clears and arms one mode for a new lifecycle.
   * @param {object} state
   * @param {object} metadata
   * @param {boolean} eligible
   * @returns {void}
   * @private
   */
  _activateState(state, metadata, eligible) {
    this._clearState(state);
    state.active = true;
    state.eligible = Boolean(eligible);
    state.metadata = metadata;
    state.startedAt = Date.now();
    state.strideBuffer = new Float32Array(this._strideSamples);
    const streaming = this._streamDecodeActive();
    state.decodeStats = createDecodeStatsRecord(metadata, eligible, streaming, state.mode);
    // The rolling window and consensus belong to window decode only. Streaming feeds
    // each stride straight to the persistent recognizer, so allocating them for a
    // streaming state burns a 64KB Float32Array plus a consensus construction (which
    // re-parses the consensus URL overrides) per lifecycle for objects nothing reads.
    // Under streaming decode that covers interventions too — they stream on their own
    // scoped recognizer; only window mode (either mode) gets both.
    if (!streaming) {
      state.rollingBuffer = new Float32Array(this._windowSamples);
      state.consensus = this._createConsensus({
        wordList: getWordList(metadata),
        ...(this._asr?.decode?.consensus || {}),
      });
    }
  }

  /**
   * Clears one mode and invalidates pending async work for it.
   * @param {object} state
   * @returns {void}
   * @private
   */
  _clearState(state) {
    state.generation += 1;
    state.active = false;
    state.eligible = false;
    state.metadata = {};
    state.rollingBuffer = null;
    state.strideBuffer = null;
    state.strideOffset = 0;
    state.samplesReceivedForActiveWindow = 0;
    state.chunkIndex = 0;
    state.consensus = null;
    state.emittedText = '';
    state.inFlight = false;
    state.processingPromise = null;
    state.latestSnapshot = null;
    state.consecutiveFailures = 0;
    state.flushedDone = false;
    state.streamStarted = false;
    state.streamDispatched = false;
    state.lastLoggedRawText = null;
    state.scoresMismatchLogged = false;
    state.startedAt = 0;
    state.rawTranscriptLog = [];
    state.rawTimeAxis = RAW_TIME_AXIS.PLACEMENT;
    // Dropping the reference only. Any decode still queued holds the record through its
    // capture and reports it on exit, so the phrase's late timings survive this clear.
    state.decodeStats = null;
    if (state.mode === LOCAL_ASR_MODE_INTERVENTION) {
      this._lastInterventionStartWarningKey = null;
    }
  }

  /**
   * Whether the handler can accept audio at all.
   * @returns {boolean}
   * @private
   */
  _canAcceptAudio() {
    return this._ready && !this._disposed && !this._paused;
  }

  /**
   * Returns state for a mode, lazily arming intervention mode when nothing is armed.
   * Intervention audio can arrive before an explicit startIntervention, so the payload
   * must carry the phrase constraint (`expectedText`/`wordList`) for the arming.
   *
   * Arming is all this does about intervention identity — it cannot tell one intervention
   * from the next (see endIntervention, which is told instead). `stalePhrase` is the
   * backstop for what the signal cannot cover: audio for a DIFFERENT phrase against a
   * still-armed intervention. Absent phraseIndex on either side means "no information",
   * not "changed".
   * @param {string} mode
   * @param {object} [metadata]
   * @returns {object}
   * @private
   */
  _getState(mode, metadata = {}) {
    if (mode === LOCAL_ASR_MODE_INTERVENTION) {
      const state = this._states[LOCAL_ASR_MODE_INTERVENTION];
      const stalePhrase = state.active
        && metadata.phraseIndex !== undefined
        && state.metadata.phraseIndex !== undefined
        && state.metadata.phraseIndex !== metadata.phraseIndex;
      if (!state.active || stalePhrase) {
        // Full teardown, not just a flush: startIntervention can decline and never reach
        // _resetState, which would leave this state active on a recognizer whose utterance
        // was just retrieved. Clearing first lets a declined re-arm retry on the next chunk.
        this.endIntervention();
        this.startIntervention(metadata);
      }
      return state;
    }
    return this._states[LOCAL_ASR_MODE_PHRASE];
  }

  /**
   * Whether incoming audio belongs to the active state.
   * @param {object} state
   * @param {object} identity
   * @returns {boolean}
   * @private
   */
  _canUseStateForAudio(state, identity) {
    if (!state.active || !state.eligible) {
      return false;
    }

    const metadata = state.metadata || {};
    return !presentMismatch(metadata.activityId, identity.activityId)
      && !presentMismatch(metadata.phraseIndex, identity.phraseIndex);
  }

  /**
   * Whether intervention audio should follow the local placement path.
   * TT-828 owns intervention routing; this guard keeps handler-side behavior
   * aligned with the current placement route instead of making all interventions
   * local by default.
   * @param {object} metadata
   * @returns {boolean}
   * @private
   */
  _shouldUseLocalAsrForIntervention(metadata = {}) {
    if (metadata.placementUsesLocalAsr !== undefined) {
      return Boolean(metadata.placementUsesLocalAsr);
    }
    return this._states[LOCAL_ASR_MODE_PHRASE].active
      ? Boolean(this._states[LOCAL_ASR_MODE_PHRASE].eligible)
      : this._lastPhraseUsedLocalAsr;
  }

  /**
   * Warns once per inactive intervention stream rejection so lazy audio chunks
   * cannot produce one warning per stride before valid intervention metadata arrives.
   * @param {string} reason
   * @param {string} message
   * @param {object} metadata
   * @returns {void}
   * @private
   */
  _warnInterventionStartFailure(reason, message, metadata = {}) {
    const key = [
      reason,
      metadata.activityId || '',
      metadata.phraseIndex ?? '',
      metadata.interventionId || '',
      metadata.expectedText || '',
      getWordList(metadata).join(' '),
    ].join('|');

    if (key === this._lastInterventionStartWarningKey) {
      return;
    }

    this._lastInterventionStartWarningKey = key;
    this._warn(message, metadata);
  }

  /**
   * Appends samples and returns transcribe work triggered by completed strides.
   * @param {object} state
   * @param {Float32Array} samples
   * @returns {Promise[]}
   * @private
   */
  _appendSamples(state, samples) {
    const work = [];
    let inputOffset = 0;

    while (inputOffset < samples.length) {
      const available = this._strideSamples - state.strideOffset;
      const toCopy = Math.min(available, samples.length - inputOffset);
      state.strideBuffer.set(
        samples.subarray(inputOffset, inputOffset + toCopy),
        state.strideOffset,
      );
      state.strideOffset += toCopy;
      state.samplesReceivedForActiveWindow += toCopy;
      inputOffset += toCopy;

      if (state.strideOffset === this._strideSamples) {
        const stride = state.strideBuffer.slice(0, this._strideSamples);
        work.push(this._advanceAndMaybeTranscribe(state, stride));
        state.strideBuffer.fill(0);
        state.strideOffset = 0;
      }
    }

    return work.filter(Boolean);
  }

  /**
   * Flushes the trailing partial stride for done:true while the phrase is active.
   *
   * The window-warmth check is window decode's alone: there, a phrase that never
   * accumulated a full window has a rolling buffer still padded with zeros, so
   * decoding it would be decoding silence. Streaming has no window to warm — it
   * decodes from the first stride — so applying that check would drop the tail of
   * every phrase shorter than one window (and decode nothing at all below one stride),
   * losing exactly the short one-word phrases where the tail IS the phrase. The
   * flushedDone and strideOffset guards are mode-independent and always apply.
   * @param {object} state
   * @returns {Promise|null}
   * @private
   */
  _flushPartialStride(state) {
    if (state.flushedDone || state.strideOffset === 0) {
      return null;
    }
    if (!this._streamDecodeActive() && state.samplesReceivedForActiveWindow < this._windowSamples) {
      return null;
    }

    const stride = state.strideBuffer.slice(0, state.strideOffset);
    state.strideBuffer.fill(0);
    state.strideOffset = 0;
    state.flushedDone = true;
    return this._advanceAndMaybeTranscribe(state, stride);
  }

  /**
   * Advances the rolling window and queues ASR when warm.
   * @param {object} state
   * @param {Float32Array} stride
   * @returns {Promise|null}
   * @private
   */
  _advanceAndMaybeTranscribe(state, stride) {
    // strideBuffer (not rollingBuffer) is the armed-state sentinel: both are nulled by
    // _clearState, but only strideBuffer is allocated in every mode — streaming phrases
    // have no rolling window (see _resetState).
    if (!stride || stride.length === 0 || !state.strideBuffer) {
      return null;
    }

    // Streaming decode: each stride is fed to the persistent recognizer exactly
    // once, from the first stride — no rolling window, no warm-up gate, no
    // re-decoded audio. The stride is already an exclusively-owned copy taken by the
    // caller, so it is handed over as-is rather than re-copied per stride.
    if (this._streamDecodeActive()) {
      return this._enqueueSnapshot(state, stride);
    }

    state.rollingBuffer.copyWithin(0, stride.length);
    state.rollingBuffer.set(stride, this._windowSamples - stride.length);

    if (state.samplesReceivedForActiveWindow < this._windowSamples) {
      return null;
    }

    const snapshot = state.rollingBuffer.slice();
    return this._enqueueSnapshot(state, snapshot);
  }

  /**
   * Queues a snapshot while keeping at most one latest pending window.
   * @param {object} state
   * @param {Float32Array} snapshot
   * @returns {Promise<void>}
   * @private
   */
  _enqueueSnapshot(state, snapshot) {
    const capture = {
      mode: state.mode,
      generation: state.generation,
      activityId: state.metadata.activityId,
      phraseIndex: state.metadata.phraseIndex,
      wordList: getWordList(state.metadata),
      biasedGraph: this._captureBiasedGraph(state),
      streaming: this._streamDecodeActive(),
      // Carried on the capture, not read off the state at completion, so a decode that
      // outlives its phrase still books its cost against the phrase that dispatched it.
      stats: state.decodeStats,
    };

    if (state.inFlight) {
      if (capture.streaming) {
        // Streaming strides are new audio, not redundant windows: dropping one
        // means the decoder never hears it. Coalesce pending strides (chunk list,
        // concatenated once at dequeue — not re-merged per stride, which is O(n²)
        // exactly on the slow devices that back up). Cap the backlog so a device
        // decoding slower than real-time sheds the oldest audio gracefully (like
        // window mode's drop-stale) instead of growing memory and latency forever.
        const chunks = state.latestSnapshot?.chunks || [];
        chunks.push(snapshot);
        let backlog = 0;
        for (let i = 0; i < chunks.length; i++) backlog += chunks[i].length;
        while (chunks.length > 1 && backlog > STREAM_BACKLOG_CAP_SAMPLES) {
          backlog -= chunks[0].length;
          chunks.shift();
          this._countQueueDrop(capture);
        }
        state.latestSnapshot = { chunks, capture };
        return state.processingPromise || Promise.resolve();
      }
      if (state.latestSnapshot) {
        this._countQueueDrop(capture);
      }
      state.latestSnapshot = { snapshot, capture };
      return state.processingPromise || Promise.resolve();
    }

    state.processingPromise = this._processSnapshot(state, snapshot, capture);
    return state.processingPromise;
  }

  /**
   * Processes a snapshot and then the latest queued snapshot, if any.
   * @param {object} state
   * @param {Float32Array} snapshot
   * @param {object} capture
   * @returns {Promise<void>}
   * @private
   */
  async _processSnapshot(state, snapshot, capture) {
    let work = { snapshot, capture };
    // This loop owns exactly one lifecycle. State objects are REUSED across phrases
    // (_clearState/_resetState mutate in place, bumping generation), so a loop whose
    // decode is still queued in the worker when endPhrase/startPhrase runs must not
    // touch the state the next phrase's loop now owns. Two ways it would:
    //
    //   1. Clearing inFlight/processingPromise on exit released the next phrase's
    //      latch, so its stride 2 started a SECOND concurrent loop — and since
    //      streamStarted only flips once stride 1's result lands, that loop sent
    //      reset: true again, flushing stride 1's audio out of the recognizer. In
    //      window mode that cost one redundant decode; under streaming decode it eats
    //      the phrase's opening stride, i.e. the slow first word this path exists to
    //      prevent, at every phrase boundary that races an in-flight decode.
    //   2. Draining latestSnapshot on capture identity alone let the stale loop adopt
    //      the NEXT phrase's queued work, interleaving two loops over one recognizer.
    //
    // Both are closed by scoping every state handoff to this generation.
    const generation = capture.generation;
    state.inFlight = true;

    while (work) {
      try {
        const transcribeOpts = { wordList: work.capture.wordList };
        // Only add biasedGraph when present (backend supports it + metadata identifies
        // the phrase), so backends without the capability see unchanged opts.
        if (work.capture.biasedGraph) {
          transcribeOpts.biasedGraph = work.capture.biasedGraph;
        }
        // Queued streaming work carries a chunk list (see _enqueueSnapshot);
        // direct work carries a single snapshot.
        const pcm = work.chunks ? concatStreamChunks(work.chunks) : work.snapshot;
        let result;
        // Recorded BEFORE the await: endPhrase and decode.phraseStats need to know a
        // chunk reached the engine even when every decode times out or throws.
        if (work.capture.stats) {
          work.capture.stats.decodeDispatched = true;
        }
        if (work.capture.streaming) {
          // streamFinish gating: endPhrase must flush even when the opening chunk is
          // still queued and streamStarted has not flipped yet.
          state.streamDispatched = true;
          if (work.capture.mode === LOCAL_ASR_MODE_INTERVENTION) {
            // An intervention's lifecycle overlaps its phrase's (the phrase stays
            // armed, mid-utterance, while the intervention listens — chunks are
            // never fed concurrently), so its claim on the engine's eviction
            // protection needs its own slot: sharing the default slot would
            // release the phrase's claim and expose its recognizer (and
            // transcript) to LRU eviction mid-utterance. The slot also scopes the
            // grammar-fallback cache key, so a fallback intervention can never
            // resolve the phrase's identically-worded recognizer.
            transcribeOpts.streamSlot = INTERVENTION_STREAM_SLOT;
          }
          result = await this._asr.streamChunk(pcm, { ...transcribeOpts, reset: !state.streamStarted });
        } else {
          result = await this._asr.transcribe(work.snapshot, transcribeOpts);
        }
        // Timing is booked unconditionally, OUTSIDE the identity guard below: the cost of
        // a decode is real whether or not its phrase is still live, and gating it on
        // _isCurrentCapture is what silently dropped the slowest decodes — the ones whose
        // phrase had already turned over precisely BECAUSE they were slow.
        this._recordDecodeTiming(work.capture, pcm, result);
        if (this._isCurrentCapture(state, work.capture)) {
          state.consecutiveFailures = 0;
          if (work.capture.streaming) {
            state.streamStarted = true;
            this._processStreamResult(state, result);
          } else {
            this._processTranscribeResult(state, result);
          }
        }
      } catch (error) {
        if (this._isCurrentCapture(state, work.capture)) {
          this._handleInferenceError(state, error);
        }
      }

      const next = state.latestSnapshot;
      if (next && next.capture.generation === generation) {
        state.latestSnapshot = null;
        work = this._isCurrentCapture(state, next.capture) ? next : null;
      } else {
        // Either nothing is queued, or what is queued belongs to a newer lifecycle —
        // leave it for the loop that owns it rather than consuming (and discarding) it.
        work = null;
      }
    }

    if (state.generation === generation) {
      state.inFlight = false;
      state.processingPromise = null;
    }

    // The phrase may have ended while this loop was still in the worker. endPhrase /
    // supersede left the reason here rather than awaiting the drain, so this is where a
    // late phrase's stats finally reach the timeline.
    const stats = capture.stats;
    if (stats && stats.pendingReason && !stats.reported) {
      this._reportDecodeStats(stats, { reason: stats.pendingReason });
    }
  }

  /**
   * Whether an async result still belongs to the active state.
   * @param {object} state
   * @param {object} capture
   * @returns {boolean}
   * @private
   */
  _isCurrentCapture(state, capture) {
    return !this._disposed
      && state.active
      && state.generation === capture.generation
      && state.mode === capture.mode
      && !presentMismatch(state.metadata.activityId, capture.activityId)
      && !presentMismatch(state.metadata.phraseIndex, capture.phraseIndex);
  }

  /**
   * Logs the engine's raw text only when it CHANGED since the last decode.
   *
   * Both decode paths run at the stride cadence (~5/sec) for as long as the phrase is
   * live, silence included — the decoder has to hear silence, since the endpoint rules
   * that commit segments and release lattice memory are trailing-silence rules. So a
   * student who stops mid-phrase produced one identical raw line per stride, which
   * buries the lines that actually mean something. The empty case was already quiet
   * (the logger drops empty text), so this covers the repeated non-empty case.
   *
   * Per-state, and reset by _clearState, so the first decode of a phrase always logs
   * even when its text matches where the previous phrase left off.
   *
   * The same change-gated snapshots accumulate on the state for the per-phrase log
   * emitted at endPhrase (see _emitPhraseTranscriptLog); the console line is
   * devtools-only and does not survive the session.
   *
   * Stamped on the placement clock so entries are directly comparable to the `timings`
   * array the final transcript is logged as. That clock is owned by ReaderManager and
   * unavailable outside a mounted read (StudentPreChecks, tests), so the fallback is
   * phrase-relative. The axis is latched on the phrase's first entry and recorded on the
   * phrase, so one array never mixes axes under a single label (see RAW_TIME_AXIS).
   * @param {object} state
   * @param {string} rawText
   * @returns {void}
   * @private
   */
  _logRawTranscript(state, rawText) {
    if (rawText === state.lastLoggedRawText) {
      return;
    }
    state.lastLoggedRawText = rawText;
    const placementTime = placementTimeMs();

    if (state.rawTranscriptLog.length === 0) {
      // First entry decides the axis for the phrase, so entries within one array are
      // always comparable to each other.
      state.rawTimeAxis = placementTime === null ? RAW_TIME_AXIS.PHRASE : RAW_TIME_AXIS.PLACEMENT;
    } else if (state.rawTimeAxis === RAW_TIME_AXIS.PLACEMENT && placementTime === null) {
      // The clock went away mid-phrase; this entry cannot be put on the latched axis.
      state.rawTimeAxis = RAW_TIME_AXIS.MIXED;
    }

    const onPlacement = state.rawTimeAxis !== RAW_TIME_AXIS.PHRASE && placementTime !== null;
    state.rawTranscriptLog.push({
      time: onPlacement
        ? placementTime
        : (state.startedAt ? Date.now() - state.startedAt : 0),
      transcript: rawText,
    });
    logLocalAsrStreamingTranscript({ mode: state.mode, rawText });
  }

  /**
   * Publishes the phrase's raw ASR snapshots with timestamps relative to phrase start.
   *
   * Shaped to match the `timings` array in `transcript_phrase_N` (`{time, transcript}`
   * entries under a `phraseText`) so a captured phrase feeds
   * scripts/transcript-simulator directly, with no reshaping step: reproducing a cursor
   * problem from a real session is otherwise a hand-reconstruction job out of truncated
   * browser console exports.
   *
   * `time` is on the placement clock, the same axis `transcript_phrase_N` stamps, so a
   * raw entry can be lined up against the final transcript entry it produced (or failed
   * to). `timeAxis` says whether that held for every entry — see RAW_TIME_AXIS.
   *
   * Emitted through a callback rather than logged here because this module deliberately
   * owns no transport — SpeechStream does the S3 write.
   * @param {object} state
   * @returns {void}
   * @private
   */
  _emitPhraseTranscriptLog(state) {
    if (state.rawTranscriptLog.length === 0) {
      return;
    }

    this._notifyCallbacks(this._phraseTranscriptLogCallbacks, {
      activityId: state.metadata.activityId,
      storyId: state.metadata.storyId,
      phraseIndex: state.metadata.phraseIndex,
      phraseText: state.metadata.expectedText,
      streaming: this._streamDecodeActive(),
      backend: this.backend,
      durationMs: state.startedAt ? Date.now() - state.startedAt : 0,
      timeAxis: state.rawTimeAxis,
      timings: state.rawTranscriptLog,
    }, 'phrase transcript log callback');
  }

  /**
   * Counts one shed queue entry against the phrase that queued it.
   * @param {object} capture
   * @returns {void}
   * @private
   */
  _countQueueDrop(capture) {
    if (capture.stats) {
      capture.stats.queueDropCount += 1;
    }
  }

  /**
   * Counts queued audio that _clearState will discard at a phrase/intervention boundary.
   * Unlike queueDropCount (mid-phrase shedding at the backlog cap), these are strides the
   * decoder would have processed had the phrase continued. Under streaming decode at
   * RTF > 1.0 this is the real cost of lag: the decoder falls behind and the boundary
   * wipes its pending work. Reports on the decode stats record so the count survives
   * the clear and reaches S3 with the deferred report.
   * @param {object} state
   * @returns {void}
   * @private
   */
  _countBoundaryDiscard(state) {
    const pending = state.latestSnapshot;
    if (!pending || !state.decodeStats) {
      return;
    }
    if (pending.chunks) {
      state.decodeStats.boundaryDiscardCount += pending.chunks.length;
    } else {
      state.decodeStats.boundaryDiscardCount += 1;
    }
  }

  /**
   * Accumulates one decode's cost against the phrase that dispatched it.
   *
   * Keyed off the capture's stats record rather than the live state, so a decode that
   * lands after its phrase ended is still counted (see createDecodeStatsRecord).
   *
   * Backends that report no `timings` (NeMo, test doubles) contribute their audio but no
   * duration. `decodeCount` therefore counts only timed decodes, and a backend that times
   * nothing reports `realTimeFactor: null` rather than a fast-looking zero.
   *
   * A backend that times only SOME decodes understates realTimeFactor, because the audio is
   * accumulated above the timing bail and so lands in RTF's denominator while its duration
   * is missing from the numerator. Deliberate: moving the accumulation below the bail would
   * zero `audioMs` for an all-untimed backend, losing the "how much audio did this phrase
   * actually feed the engine" signal that is useful on its own. Read realTimeFactor
   * alongside `decodeCount` — if it is below the stride count, RTF is a lower bound.
   * @param {object} capture
   * @param {Float32Array} pcm Audio actually fed to the engine for this decode.
   * @param {object} result
   * @returns {void}
   * @private
   */
  _recordDecodeTiming(capture, pcm, result) {
    const stats = capture.stats;
    if (!stats) {
      return;
    }
    // Streaming feeds one stride per decode; window mode re-decodes the whole rolling
    // buffer each stride but only advances real time by one stride — counting pcm.length
    // there inflates audioMs ~window/stride and makes RTF look ~5× healthier than it is.
    stats.audioSamplesDecoded += capture.streaming
      ? (pcm?.length || 0)
      : this._strideSamples;
    const total = result?.timings?.total;
    if (typeof total !== 'number' || !Number.isFinite(total)) {
      return;
    }
    stats.decodeCount += 1;
    stats.decodeMsTotal += total;
    stats.decodeMsMax = Math.max(stats.decodeMsMax, total);
    const timeoutMs = result.timings.timeoutMs;
    if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
      stats.decodeTimeoutBudgetSeen = true;
      // total spans the flush and the accept; each awaited the full budget, so count on
      // whichever grazed it. See DECODE_TIMEOUT_NEAR_MISS_RATIO. flushMs is 0 on a
      // non-reset stride, so this reduces to `total` there.
      const flushMs = Number.isFinite(result.timings.flushMs) ? result.timings.flushMs : 0;
      const worstRegionMs = Math.max(flushMs, total - flushMs);
      if (worstRegionMs >= timeoutMs * DECODE_TIMEOUT_NEAR_MISS_RATIO) {
        stats.decodeTimeoutNearMisses += 1;
      }
    }
    if (stats.firstDecodeMs === null) {
      stats.firstDecodeMs = Math.round(total);
    }
    const stageMs = result.timings.stageMs;
    if (typeof stageMs === 'number' && Number.isFinite(stageMs)) {
      stats.stageMsTotal += stageMs;
      if (result.timings.staged) {
        stats.stagedCount += 1;
      }
    }
  }

  /**
   * Closes out a phrase's decode accounting without blocking the caller.
   *
   * Reports immediately when the decode queue is idle. When a decode is still in the
   * worker, records the reason and returns: _processSnapshot reports on exit, once the
   * late timings are in. This is why the whole arm/disarm path can stay synchronous —
   * see createDecodeStatsRecord for what awaiting here cost instead.
   * @param {object} state
   * @param {'ended'|'tornDown'|'superseded'} reason
   * @returns {void}
   * @private
   */
  _finalizeDecodeStats(state, reason) {
    const stats = state.decodeStats;
    if (!stats || stats.reported) {
      return;
    }
    if (state.inFlight) {
      stats.pendingReason = reason;
      return;
    }
    this._reportDecodeStats(stats, { reason });
  }

  /**
   * Reports the phrase's decode cost to the lifecycle timeline.
   *
   * This is the number that was missing when a student on a slow Chromebook got no
   * transcripts: warmup's `tierUpFactor` measures the word-list grammar on synthetic noise,
   * not the per-phrase biased HCLG that serves real reading, and the drop count alone
   * says audio was shed without saying whether staging or decoding shed it. `realTimeFactor`
   * above 1 means the device cannot keep pace with the student and the backlog can only
   * grow — the condition that makes a phrase silent (threshold is 1 in both modes).
   * Under window decode each decode re-hears a full buffer but only one stride of new
   * audio arrives, so `audioMs` counts stride advances. Window phrases also carry
   * `strideWindowRatio` (stride/window overlap) for readers comparing against the
   * overlapped-buffer mental model — it is not a second keep-up threshold.
   *
   * Emitted for every eligible phrase that decoded or attempted decode (including all-
   * failure phrases with shed audio), because the comparison between a good phrase and a
   * bad one on the SAME device is what separates "this hardware is too slow" from "this
   * phrase hit a stall". Suppressing an all-zero event is right; suppressing a phrase where
   * every decode timed out is not — that is the motivating "no transcripts" case.
   *
   * Also flushed from dispose() (reason `tornDown`) and phrase supersede via startPhrase
   * (reason `superseded`) so stats are not discarded when endPhrase never runs — the
   * inference-failure teardown path skips endPhrase and goes straight to dispose().
   *
   * Triage: `queueDropCount > 0` with a low `realTimeFactor` usually means the decode
   * queue was blocked (a warm, a cold stage, or a slow window held `_tail`) rather
   * than a uniformly slow decoder — check `graphs.prewarmed`, `coldStages`, and
   * per-decode `stageMs` on the same timeline. (Warm FAILURES report as
   * `graphs.phraseFallback` with reason `buildFailed`; the v2-era
   * `graphs.prewarmFailed` event no longer exists.) `decodeDispatched: true` with
   * `decodeCount: 0` means work reached the engine but every decode timed out or
   * threw.
   * @param {object} stats A decode stats record (see createDecodeStatsRecord).
   * @param {{reason?: 'ended'|'tornDown'|'superseded'}} [options]
   * @returns {void}
   * @private
   */
  _reportDecodeStats(stats, { reason = 'ended' } = {}) {
    if (stats.reported) {
      return;
    }
    const streaming = stats.streaming;
    const hadDecodeActivity = stats.decodeCount > 0
      || stats.decodeDispatched
      || stats.queueDropCount > 0;
    if (!stats.eligible || !hadDecodeActivity) {
      return;
    }
    const audioMs = Math.round((stats.audioSamplesDecoded / this._sampleRate) * 1000);
    // Separate event per mode; see createDecodeStatsRecord for why they cannot share one.
    const event = stats.mode === LOCAL_ASR_MODE_INTERVENTION
      ? 'decode.interventionStats'
      : 'decode.phraseStats';
    recordLocalAsrLifecycle(event, {
      reason,
      activityId: stats.activityId,
      phraseIndex: stats.phraseIndex,
      streaming,
      decodeDispatched: stats.decodeDispatched,
      decodeCount: stats.decodeCount,
      decodeMsTotal: Math.round(stats.decodeMsTotal),
      decodeMsMax: Math.round(stats.decodeMsMax),
      // Decodes past DECODE_TIMEOUT_NEAR_MISS_RATIO of the backend's timeout budget:
      // the early-warning counterpart of an inference failure (TT-1173). null when no
      // decode reported a budget (NeMo) — same rule as realTimeFactor below.
      decodeTimeoutNearMisses: stats.decodeTimeoutBudgetSeen ? stats.decodeTimeoutNearMisses : null,
      firstDecodeMs: stats.firstDecodeMs,
      audioMs,
      // Decode wall time per unit of audio-time advanced. >1 cannot keep pace (both modes).
      realTimeFactor: (stats.decodeCount > 0 && audioMs > 0)
        ? +(stats.decodeMsTotal / audioMs).toFixed(2)
        : null,
      ...(!streaming && this._windowSamples > 0
        ? { strideWindowRatio: +(this._strideSamples / this._windowSamples).toFixed(3) }
        : {}),
      // Recognizer staging: building one (biased graph load plus acoustic-model re-parse,
      // or a composed word-list grammar) rather than reusing a cached one. It shares the
      // decode queue, so time here stalls the phrase's opening audio without appearing in
      // decodeMsTotal. coldStages counts how many decodes paid it.
      stageMsTotal: Math.round(stats.stageMsTotal),
      coldStages: stats.stagedCount,
      // Streaming: strides shed from the backlog the decoder never heard. Window: superseded
      // rolling windows (benign — the next window covers the same audio).
      queueDropCount: stats.queueDropCount,
      droppedMs: (streaming && stats.queueDropCount > 0)
        ? Math.round((stats.queueDropCount * this._strideSamples / this._sampleRate) * 1000)
        : 0,
      // Strides/windows still queued when the phrase ended, wiped by _clearState. Unlike
      // queueDropCount these would have been decoded had the phrase continued — the direct
      // cost of RTF > 1.0 at phrase boundaries.
      boundaryDiscardCount: stats.boundaryDiscardCount,
      boundaryDiscardMs: (streaming && stats.boundaryDiscardCount > 0)
        ? Math.round((stats.boundaryDiscardCount * this._strideSamples / this._sampleRate) * 1000)
        : 0,
    });
    stats.reported = true;
  }

  /**
   * Emits a streaming-decode result: the cumulative transcript (committed
   * segments + live partial). The per-phrase biased HCLG already constrains
   * output, so the rolling-window consensus is bypassed.
   *
   * Nothing smooths emission on this path: a Kaldi endpoint can commit several words
   * in one chunk, and the cursor advances that far in one step. That is the accepted
   * trade — keep the student moving forward rather than hold them on a word — and
   * ReaderManager's error array only grows, so the cursor cannot walk backwards.
   * Paced emission (#5850) would drain multi-word commits one per tick, but it is not
   * on this branch; landing it is a separate decision, not a dependency of this path.
   * @param {object} state
   * @param {object} result
   * @returns {void}
   * @private
   */
  _processStreamResult(state, result = {}) {
    const text = (result.text || '').trim();

    this._logRawTranscript(state, text);
    if (text) {
      this._emitTranscript(state, text);
    }
  }

  /**
   * Sends accepted ASR output through streaming consensus.
   * @param {object} state
   * @param {object} result
   * @returns {void}
   * @private
   */
  _processTranscribeResult(state, result = {}) {
    if (!state.consensus) {
      return;
    }

    const chunkIndex = ++state.chunkIndex;
    const rawText = result.text || '';

    // Mismatch telemetry: detect score/word count divergence (once per phrase).
    if (result.wordScores && !state.scoresMismatchLogged) {
      const wordCount = rawText.trim().split(/\s+/).filter(Boolean).length;
      if (wordCount !== result.wordScores.length) {
        state.scoresMismatchLogged = true;
        this._logInfo('ASR score/word count mismatch', {
          phraseIndex: state.metadata.phraseIndex,
          wordCount,
          scoresCount: result.wordScores.length,
          text: rawText,
        });
      }
    }

    const outcome = state.consensus.processChunk(
      rawText,
      result.wordScores,
      chunkIndex,
    );

    this._logRawTranscript(state, rawText);

    if (outcome.emitted) {
      this._emitTranscript(state, state.consensus.text);
    }
  }

  /**
   * Records every transcribe failure to the lifecycle timeline and tears down local
   * ASR when they run consecutively.
   *
   * A rejection marked `biasedQualityFallback` is a worker-side biased-graph
   * create failure (voskAsr's error listener): the phrase's key is already
   * latched, so the NEXT window decodes on the word-list grammar — a quality
   * downgrade, not a runtime-health signal. It is logged but deliberately does
   * not count toward the consecutive-failure teardown: at most one such
   * rejection can occur per phrase key, and letting it share the counter with
   * real inference failures would let one bad published FST plus two unrelated
   * faults end local ASR for the session.
   * @param {object} state
   * @param {Error} error
   * @returns {void}
   * @private
   */
  _handleInferenceError(state, error) {
    // Flatten once: a raw Error serializes to {} in the debug-log export, so every sink
    // below — consoleLogger (via _warn) and the lifecycle timeline — needs the string,
    // not the Error object.
    const errorMsg = error?.message || (error != null ? String(error) : undefined);
    if (error && error.biasedQualityFallback) {
      this._warn('Local ASR biased-graph create failed; window lost, phrase falls back to the word-list grammar', {
        mode: state.mode,
        activityId: state.metadata.activityId,
        phraseIndex: state.metadata.phraseIndex,
        error: errorMsg,
      });
      return;
    }
    state.consecutiveFailures += 1;

    // Bound the log to the first N failures per activity (see MAX_LOGGED_INFERENCE_FAILURES):
    // the teardown latch bounds only CONSECUTIVE failures, so a flapping runtime would
    // otherwise log every failure for the whole session. Reset the budget when the activity
    // changes.
    const activityId = state.metadata.activityId;
    if (activityId !== this._loggedFailureActivityId) {
      this._loggedFailureActivityId = activityId;
      this._loggedFailureCount = 0;
    }
    this._loggedFailureCount += 1;

    if (this._loggedFailureCount <= MAX_LOGGED_INFERENCE_FAILURES) {
      // Two sinks by design. _warn keeps the searchable 'Local ASR inference failed' string
      // on the INJECTED logger — recordLocalAsrFailure below mirrors to the GLOBAL console
      // under the event name 'runtime.inferenceFailed', so on its own it neither preserves
      // that string (used in the TT-1158 diagnosis) nor honours the handler's logger option.
      // recordLocalAsrFailure is the durable half: it reaches the lifecycle timeline, where
      // reporting only at the teardown threshold used to leave a 1-2 failure session no
      // recoverable cause at all (TT-1173).
      this._warn('Local ASR inference failed', {
        mode: state.mode,
        activityId,
        phraseIndex: state.metadata.phraseIndex,
        count: state.consecutiveFailures,
        error: errorMsg,
      });
      recordLocalAsrFailure('runtime.inferenceFailed', {
        mode: state.mode,
        activityId,
        phraseIndex: state.metadata.phraseIndex,
        count: state.consecutiveFailures,
        error: errorMsg,
      });
    } else if (this._loggedFailureCount === MAX_LOGGED_INFERENCE_FAILURES + 1) {
      // One marker that the activity kept failing past the budget, then silence — enough to
      // say "still failing" without the unbounded tail. The _warn keeps the searchable
      // 'Local ASR inference failed' substring on the injected logger, so a debug-export
      // grep sees the truncation in band rather than silently counting exactly N and
      // stopping; recordLocalAsrFailure is the durable timeline half.
      this._warn('Local ASR inference failed (capped)', {
        mode: state.mode,
        activityId,
        recorded: MAX_LOGGED_INFERENCE_FAILURES,
      });
      recordLocalAsrFailure('runtime.inferenceFailuresCapped', {
        mode: state.mode,
        activityId,
        recorded: MAX_LOGGED_INFERENCE_FAILURES,
      });
    }

    if (state.consecutiveFailures >= MAX_CONSECUTIVE_INFERENCE_FAILURES) {
      this._emitError({
        reason: LOCAL_ASR_FALLBACK_REASON.INFERENCE_FAILURE,
        mode: state.mode,
        count: state.consecutiveFailures,
        error,
      });
    }
  }

  /**
   * Emits a transcript through the mode-specific callback set.
   *
   * Intervention transcripts reduce to a single token, target-preferring — see
   * pickInterventionToken for why WHICH token is load-bearing. Normalization goes through
   * the shared tokenizer (asr/wordNormalize), not a local character class: the class this
   * replaced used `\w`, which is ASCII-only, so it stripped the accents the vocabulary
   * stores unfolded and emitted "piñata" as "piata".
   *
   * UNK tokens are dropped BEFORE normalizeWord, on the raw tokens: the streaming path
   * deliberately keeps `<UNK>` (ReaderManager's suppression signal reads it), and
   * normalizeWord('<UNK>') yields the bare word `unk`. An all-UNK transcript emits nothing.
   * @param {object} state
   * @param {string} text
   * @returns {void}
   * @private
   */
  _emitTranscript(state, text) {
    if (!text || this._disposed) {
      return;
    }

    let emitText = text;
    if (state.mode === LOCAL_ASR_MODE_INTERVENTION) {
      const rawTokens = text.split(/\s+/).filter((token) => token && !isUnkToken(token));
      const normalized = normalizeWord(rawTokens.join(' '));
      const words = normalized.split(/\s+/).filter(Boolean);
      emitText = pickInterventionToken(words, getInterventionTarget(state.metadata));
    }

    if (!emitText || emitText === state.emittedText) {
      return;
    }

    state.emittedText = emitText;
    const callbacks = state.mode === LOCAL_ASR_MODE_INTERVENTION
      ? this._interventionTranscriptCallbacks
      : this._transcriptCallbacks;
    this._notifyCallbacks(callbacks, emitText, 'transcript callback');
  }

  /**
   * Emits readiness to subscribers.
   * @param {boolean} ready
   * @returns {void}
   * @private
   */
  _emitReady(ready) {
    this._notifyCallbacks(this._readyCallbacks, ready, 'ready callback');
  }

  /**
   * Emits errors to subscribers and logger.
   * @param {object} error
   * @returns {void}
   * @private
   */
  _emitError(error) {
    this._notifyCallbacks(this._errorCallbacks, error, 'error callback');
    this._warn('Local ASR handler error', error);
  }

  /**
   * Notifies subscribers without letting consumer errors affect ASR state.
   * @param {Set<Function>} callbacks
   * @param {*} payload
   * @param {string} label
   * @returns {void}
   * @private
   */
  _notifyCallbacks(callbacks, payload, label) {
    callbacks.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        this._warn(`Local ASR ${label} error`, error);
      }
    });
  }

  /**
   * Logs warnings without allowing logger failures to affect ASR state.
   * @param {string} message
   * @param {*} payload
   * @returns {void}
   * @private
   */
  _warn(message, payload) {
    if (this._logger && typeof this._logger.warn === 'function') {
      try {
        this._logger.warn(message, payload);
      } catch (error) {
        // Logging must never affect audio or transcript lifecycle.
      }
    }
  }

  /**
   * Logs structured info when the caller provides an info-capable logger.
   * @param {string} message
   * @param {*} payload
   * @returns {void}
   * @private
   */
  _logInfo(message, payload) {
    if (this._logger && typeof this._logger.info === 'function') {
      try {
        this._logger.info(message, payload);
      } catch (error) {
        // Logging must never affect audio or transcript lifecycle.
      }
    }
  }
}
